import React, { useState, useEffect, useRef, useMemo } from "react";
import { shallowEqual, useSelector } from "react-redux";
import {
  Card,
  Card as CardMaterial,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  Typography,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  CardMedia,
  Tooltip,
  createTheme,
  IconButton,
  Chip,
  MenuItem,
} from "@material-ui/core";
import { useHistory, useParams, useLocation } from "react-router-dom";
import {
  deleteCongresoEdicion,
  getCongresoEdicionById,
  postCongresoEdicion,
  updateCongresoEdicion,
} from "../../../../api/congresoEdicion";
import { uploadSingleFile } from "../../../../api/files";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import {
  isCongresoDelegado,
  canManageCongresoDelegadosUi,
  canEditGestionModule,
  isReadOnlyConsultor,
} from "../../../../_helpers/backofficePermissions";
import CongresoShareDialog from "./CongresoShareDialog";
import MultilanguageTabBlock from "../../../components/MultilanguageTabBlock";
import Editor from "../../../components/editor/Editor";
import { SERVER_URL } from "../../../../api";
import PreviewDialog from "../../../components/dialogs/PreviewDialog";
import { Delete, Visibility, Add as AddIcon } from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ShareIcon from "@material-ui/icons/Share";
import SaveOutlinedIcon from "@material-ui/icons/SaveOutlined";
import DashboardOutlinedIcon from "@material-ui/icons/DashboardOutlined";
import MailOutlineIcon from "@material-ui/icons/MailOutline";
import GroupWorkOutlinedIcon from "@material-ui/icons/GroupWorkOutlined";
import EventOutlinedIcon from "@material-ui/icons/EventOutlined";
import SchoolOutlinedIcon from "@material-ui/icons/SchoolOutlined";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import PlaceOutlinedIcon from "@material-ui/icons/PlaceOutlined";
import HowToRegOutlinedIcon from "@material-ui/icons/HowToRegOutlined";
import BusinessOutlinedIcon from "@material-ui/icons/BusinessOutlined";
import SupervisorAccountOutlinedIcon from "@material-ui/icons/SupervisorAccountOutlined";
import SecurityOutlinedIcon from "@material-ui/icons/SecurityOutlined";
import LinkOutlinedIcon from "@material-ui/icons/LinkOutlined";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import ImageOutlinedIcon from "@material-ui/icons/ImageOutlined";
import PaletteOutlinedIcon from "@material-ui/icons/PaletteOutlined";
import TuneOutlinedIcon from "@material-ui/icons/TuneOutlined";
import SubjectOutlinedIcon from "@material-ui/icons/Subject";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import CloudUploadOutlinedIcon from "@material-ui/icons/CloudUploadOutlined";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import PhotoCameraOutlinedIcon from "@material-ui/icons/PhotoCameraOutlined";
import RestoreOutlinedIcon from "@material-ui/icons/RestoreOutlined";
import BrandingWatermarkOutlinedIcon from "@material-ui/icons/BrandingWatermarkOutlined";
import WebOutlinedIcon from "@material-ui/icons/WebOutlined";
import CongresoProgramaCalendario from "./CongresoProgramaCalendario";
import CongresoTalleresEditor from "./CongresoTalleresEditor";
import { normalizeCongresoAnchoGrilla, anchoGrillaToSpan12 } from "./congresoAnchoGrilla";
import { sortActividadesPorHora, sortFranjasPorHora } from "./congresoHoraSort";
import "./congresoEdicion.scss";

function isHex6(str) {
  return !!(str && /^#[0-9A-Fa-f]{6}$/.test(String(str).trim()));
}

const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

/** Píxeles recomendados (referencia web congresos). Se muestran en negrita junto al control de subida. */
const IMG_TAM_CONGRESO = {
  cabecera: "1920 × 800 px",
  logoEdicion: "600 × 180 px",
  cronograma: "1920 × 700 px",
  sedeLateral: "1000 × 700 px",
  comiteMiembro: "300 × 300 px",
  logoOrganizador: "480 × 180 px",
  logoPatrocinador: "640 × 220 px",
  logoSponsor: "800 × 260 px",
};

/** Oculto de momento en pestaña «Información general». */
const INFORMACION_GENERAL_MOSTRAR_ORGANIZACION = false;

const FOOTER_I18N_LANGS = ["es", "ca", "en"];

/**
 * Textos por defecto del pie de la web del congreso.
 * Mantener alineado con `webcongresos-actedi/src/components/layout/Footer.jsx` (FOOTER_COPY + DEFAULT_TEL_DIGITS).
 */
const FOOTER_FORM_DEFAULTS_BY_LANG = {
  es: {
    footerTituloEnlaces: "Enlaces útiles",
    footerTituloLegal: "Legal",
    footerTituloContacto: "Contacto",
    footerEnlaceCongreso: "Este congreso",
    footerEnlaceContactoCongreso: "Contacto del congreso",
    footerEnlaceInscripcion: "Inscripción",
    footerEnlaceActediWeb: "Web ACTEDI",
    footerTelefono: "630 380 886",
    footerDireccion: "Calle Marina, 27 Bajos 08005 - Barcelona",
    footerSellosTitulo: "Sellos y acreditaciones",
  },
  ca: {
    footerTituloEnlaces: "Enllaços útils",
    footerTituloLegal: "Legal",
    footerTituloContacto: "Contacte",
    footerEnlaceCongreso: "Aquest congrés",
    footerEnlaceContactoCongreso: "Contacte del congrés",
    footerEnlaceInscripcion: "Inscripció",
    footerEnlaceActediWeb: "Web ACTEDI",
    footerTelefono: "630 380 886",
    footerDireccion: "C. Marina, 27 Baixos 08005 - Barcelona",
    footerSellosTitulo: "Segells i acreditacions",
  },
  en: {
    footerTituloEnlaces: "Quick links",
    footerTituloLegal: "Legal",
    footerTituloContacto: "Contact",
    footerEnlaceCongreso: "This congress",
    footerEnlaceContactoCongreso: "Congress contact",
    footerEnlaceInscripcion: "Registration",
    footerEnlaceActediWeb: "ACTEDI website",
    footerTelefono: "+34 630 380 886",
    footerDireccion: "C. Marina 27, ground floor · 08005 Barcelona",
    footerSellosTitulo: "Seals & accreditation",
  },
};

const FOOTER_DEFAULT_TEL_DIGITS = "34630380886";

const FOOTER_LEGACY_ENLACE_KEYS = [
  "footerEnlaceCongreso",
  "footerEnlaceContactoCongreso",
  "footerEnlaceInscripcion",
  "footerEnlaceActediWeb",
];

const ACTEDI_WEB_ORIGIN = "https://actedi.cat";

const ACTEDI_HOME_BY_LANG = {
  es: `${ACTEDI_WEB_ORIGIN}/es`,
  ca: `${ACTEDI_WEB_ORIGIN}/`,
  en: `${ACTEDI_WEB_ORIGIN}/en`,
};

const ACTEDI_LEGAL_ROUTES = {
  about: { es: "/es/sobre-nosotros", ca: "/sobre-nosaltres", en: "/en/about" },
  privacy: { es: "/es/politica-privacidad", ca: "/politica-privacitat", en: "/en/privacy-policy" },
  legalNotice: { es: "/es/aviso-legal", ca: "/avis-legal", en: "/en/legal-notice" },
  cookies: { es: "/es/politica-cookies", ca: "/politica-cookies", en: "/en/cookie-policy" },
  terms: {
    es: "/es/condiciones-generales-contratacion",
    ca: "/condicions-general-contratatcio",
    en: "/en/generic-terms-of-contraction",
  },
};

function actediLegalUrl(routeKey, lang) {
  const path = (ACTEDI_LEGAL_ROUTES[routeKey] && ACTEDI_LEGAL_ROUTES[routeKey][lang]) || "";
  return path ? `${ACTEDI_WEB_ORIGIN}${path}` : ACTEDI_WEB_ORIGIN;
}

function footerUrlI18nSame(pathTemplate, year) {
  const y = year != null && Number.isFinite(Number(year)) ? String(Number(year)) : "{year}";
  const p = String(pathTemplate).replace(/\{year\}/g, y);
  return { es: p, ca: p, en: p };
}

function footerUrlI18nPerLang(mapByLang) {
  const o = {};
  for (const lang of FOOTER_I18N_LANGS) o[lang] = String(mapByLang[lang] || "").trim();
  return o;
}

/** Alineado con `webcongresos-actedi/src/utils/congresoFooterLinks.js`. */
function buildDefaultFooterEnlacesUtiles(year) {
  const y = year != null && Number.isFinite(Number(year)) ? Number(year) : "{year}";
  return [
    {
      etiqueta: { es: "Este congreso", ca: "Aquest congrés", en: "This congress" },
      url: footerUrlI18nSame(`/{lang}/${y}`, y),
    },
    {
      etiqueta: {
        es: "Contacto del congreso",
        ca: "Contacte del congrés",
        en: "Congress contact",
      },
      url: footerUrlI18nSame(`/{lang}/${y}/contacto`, y),
    },
    {
      etiqueta: { es: "Inscripción", ca: "Inscripció", en: "Registration" },
      url: footerUrlI18nSame(`/{lang}/${y}/inscripcion`, y),
    },
    {
      etiqueta: { es: "Web ACTEDI", ca: "Web ACTEDI", en: "ACTEDI website" },
      url: footerUrlI18nPerLang(ACTEDI_HOME_BY_LANG),
    },
  ];
}

function buildDefaultFooterEnlacesLegal() {
  return [
    {
      etiqueta: { es: "Sobre nosotros", ca: "Sobre nosaltres", en: "About us" },
      url: footerUrlI18nPerLang({
        es: actediLegalUrl("about", "es"),
        ca: actediLegalUrl("about", "ca"),
        en: actediLegalUrl("about", "en"),
      }),
    },
    {
      etiqueta: {
        es: "Política de privacidad",
        ca: "Política de privacitat",
        en: "Privacy policy",
      },
      url: footerUrlI18nPerLang({
        es: actediLegalUrl("privacy", "es"),
        ca: actediLegalUrl("privacy", "ca"),
        en: actediLegalUrl("privacy", "en"),
      }),
    },
    {
      etiqueta: { es: "Aviso legal", ca: "Avís legal", en: "Legal notice" },
      url: footerUrlI18nPerLang({
        es: actediLegalUrl("legalNotice", "es"),
        ca: actediLegalUrl("legalNotice", "ca"),
        en: actediLegalUrl("legalNotice", "en"),
      }),
    },
    {
      etiqueta: {
        es: "Política de cookies",
        ca: "Política de cookies",
        en: "Cookie policy",
      },
      url: footerUrlI18nPerLang({
        es: actediLegalUrl("cookies", "es"),
        ca: actediLegalUrl("cookies", "ca"),
        en: actediLegalUrl("cookies", "en"),
      }),
    },
    {
      etiqueta: {
        es: "Condiciones generales de contratación",
        ca: "Condicions generals de contractació",
        en: "Generic terms of contract",
      },
      url: footerUrlI18nPerLang({
        es: actediLegalUrl("terms", "es"),
        ca: actediLegalUrl("terms", "ca"),
        en: actediLegalUrl("terms", "en"),
      }),
    },
  ];
}

function normalizeFooterUrlField(raw) {
  if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    const o = {};
    for (const lang of FOOTER_I18N_LANGS) o[lang] = String(raw[lang] || "").trim();
    return o;
  }
  const s = String(raw || "").trim();
  return { es: s, ca: s, en: s };
}

function normalizeFooterEnlacesList(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((row) => ({
    etiqueta:
      row?.etiqueta && typeof row.etiqueta === "object" ? { ...row.etiqueta } : {},
    url: normalizeFooterUrlField(row?.url),
  }));
}

function footerEnlaceRowHasContent(row) {
  if (!row || typeof row !== "object") return false;
  const hasLabel = FOOTER_I18N_LANGS.some((l) => String(row.etiqueta?.[l] || "").trim());
  const hasUrl = FOOTER_I18N_LANGS.some((l) => String(row.url?.[l] || "").trim());
  return hasLabel || hasUrl;
}

function buildFooterEnlacesUtilesFromLegacy(base, year) {
  const list = buildDefaultFooterEnlacesUtiles(year);
  FOOTER_LEGACY_ENLACE_KEYS.forEach((field, idx) => {
    const obj = base[field];
    if (!obj || typeof obj !== "object" || !list[idx]) return;
    for (const lang of FOOTER_I18N_LANGS) {
      const v = String(obj[lang] || "").trim();
      if (v) list[idx].etiqueta[lang] = v;
    }
  });
  return list;
}

function legacyFooterEnlacesCustomized(base) {
  return FOOTER_LEGACY_ENLACE_KEYS.some((field) => {
    const obj = base[field];
    if (!obj || typeof obj !== "object") return false;
    return FOOTER_I18N_LANGS.some((lang) => {
      const v = String(obj[lang] || "").trim();
      const def = String(FOOTER_FORM_DEFAULTS_BY_LANG[lang][field] || "").trim();
      return v && v !== def;
    });
  });
}

const FOOTER_I18N_FIELD_KEYS = [
  "footerTituloEnlaces",
  "footerTituloLegal",
  "footerTituloContacto",
  "footerTelefono",
  "footerDireccion",
  "footerSellosTitulo",
];

function getDefaultFooterFormState(year) {
  const y =
    year != null && Number.isFinite(Number(year)) ? Number(year) : new Date().getFullYear();
  const o = {};
  for (const field of FOOTER_I18N_FIELD_KEYS) {
    o[field] = {};
    for (const lang of FOOTER_I18N_LANGS) {
      o[field][lang] = FOOTER_FORM_DEFAULTS_BY_LANG[lang][field] || "";
    }
  }
  o.footerTelefonoE164 = FOOTER_DEFAULT_TEL_DIGITS;
  o.footerMapsBusqueda = FOOTER_FORM_DEFAULTS_BY_LANG.es.footerDireccion;
  o.footerEnlacesUtiles = buildDefaultFooterEnlacesUtiles(y);
  o.footerEnlacesLegal = buildDefaultFooterEnlacesLegal();
  return o;
}

/** Rellena idiomas / teléfono / mapa vacíos tras cargar desde API (misma lógica que la web). */
function ensureFooterDefaultsInPlace(base) {
  if (!base || typeof base !== "object") return;
  for (const field of FOOTER_I18N_FIELD_KEYS) {
    const cur = base[field] && typeof base[field] === "object" ? { ...base[field] } : {};
    for (const lang of FOOTER_I18N_LANGS) {
      if (!String(cur[lang] || "").trim()) {
        cur[lang] = FOOTER_FORM_DEFAULTS_BY_LANG[lang][field] || "";
      }
    }
    base[field] = cur;
  }
  const tel = String(base.footerTelefonoE164 != null ? base.footerTelefonoE164 : "").replace(/\D/g, "");
  base.footerTelefonoE164 = tel || FOOTER_DEFAULT_TEL_DIGITS;
  const maps = String(base.footerMapsBusqueda != null ? base.footerMapsBusqueda : "").trim();
  base.footerMapsBusqueda = maps || FOOTER_FORM_DEFAULTS_BY_LANG.es.footerDireccion;
  const yearNum = Number(base.year);
  const yearForLinks =
    Number.isInteger(yearNum) && yearNum >= 2000 && yearNum < 2100 ? yearNum : new Date().getFullYear();
  let utiles = normalizeFooterEnlacesList(base.footerEnlacesUtiles).filter(footerEnlaceRowHasContent);
  if (!utiles.length) {
    utiles = legacyFooterEnlacesCustomized(base)
      ? buildFooterEnlacesUtilesFromLegacy(base, yearForLinks)
      : buildDefaultFooterEnlacesUtiles(yearForLinks);
  }
  base.footerEnlacesUtiles = utiles;
  let legal = normalizeFooterEnlacesList(base.footerEnlacesLegal).filter(footerEnlaceRowHasContent);
  if (!legal.length) legal = buildDefaultFooterEnlacesLegal();
  base.footerEnlacesLegal = legal;
}

function GeneralApartado({ Icon, title, children }) {
  return (
    <section className="congreso-edicion-general-bloque">
      <header className="congreso-edicion-general-bloque__head">
        {Icon ? <Icon className="congreso-edicion-general-bloque__icon" /> : null}
        <Typography variant="h6" component="h2" className="congreso-edicion-general-bloque__title">
          {title}
        </Typography>
      </header>
      <hr className="congreso-edicion-general-bloque__rule" />
      <div className="congreso-edicion-general-bloque__body">{children}</div>
    </section>
  );
}

function normalizeMiembro(mem) {
  const rawR = mem?.redesSociales;
  let redesSociales = [];
  if (Array.isArray(rawR)) {
    redesSociales = rawR
      .map((r) => ({
        url: typeof r === "string" ? String(r).trim() : String(r?.url || "").trim(),
      }))
      .filter((r) => r.url);
  }
  return {
    texto: mem?.texto && typeof mem.texto === "object" ? { ...mem.texto } : {},
    foto: String(mem?.foto || "").trim(),
    redesSociales,
  };
}

function normalizeComites(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((c) => ({
    nombre: c?.nombre && typeof c.nombre === "object" ? { ...c.nombre } : {},
    miembros: Array.isArray(c?.miembros) ? c.miembros.map(normalizeMiembro) : [],
    anchoGrilla: normalizeCongresoAnchoGrilla(c?.anchoGrilla, 50),
  }));
}

/** Comités por defecto al crear una edición nueva (1/3 + 2/3 en la misma fila). */
function organizadoresPorDefecto() {
  return [{ nombre: "ACTEDI", logo: "", url: "" }];
}

function comitesPorDefectoNuevaEdicion() {
  return [
    {
      nombre: {
        es: "Comité Organizador",
        ca: "Comitè Organitzador",
        en: "Organizing Committee",
      },
      miembros: [],
      anchoGrilla: 33,
    },
    {
      nombre: {
        es: "Comité Científico",
        ca: "Comitè Científic",
        en: "Scientific Committee",
      },
      miembros: [],
      anchoGrilla: 66,
    },
  ];
}

function getEmptyEdicion(options = {}) {
  const conComitesPorDefecto = options.conComitesPorDefecto === true;
  return {
    year: new Date().getFullYear(),
    isDefault: false,
    active: true,
    published: false,
    titulo: "",
    heroBadge: {},
    heroSubtitle: {},
    heroSede: {},
    heroFechas: {},
    headerImage: "",
    editionLogo: "",
    contactoEmail: "actedi@actedi.cat",
    organizadores: organizadoresPorDefecto(),
    themeNavBackground: "",
    themeNavAccent: "",
    themeAccentSecondary: "",
    cartaBienvenida: {},
    cronogramaImage: "",
    inscripcionPlanes: [],
    comitesHtml: {},
    comites: conComitesPorDefecto ? comitesPorDefectoNuevaEdicion() : [],
    programaDetalladoHtml: {},
    normativaHtml: {},
    envioResumenHtml: {},
    premiosHtml: {},
    guiaBuenasPracticasHtml: {},
    guiaModeradoresHtml: {},
    guiaModeradoresPlantillaUrl: { es: "", ca: "", en: "" },
    guiaPonentesHtml: {},
    guiaPonentesPlantillaUrl: { es: "", ca: "", en: "" },
    guiaBuenasPracticasItems: [],
    guiaModeradoresItems: [],
    guiaNormasItems: [],
    guiaPonentesItems: [],
    guiaEnvioTrabajosItems: [],
    informacionGeneralBloques: [],
    selloEtico: { descripcion: {}, logo: "", url: "" },
    selloAcreditacion: { descripcion: {}, logo: "", url: "" },
    sedeBloque: emptySedeBloque(),
    sedeComoLlegarHtml: {},
    posterPublicitario: "",
    organizacionIntroHtml: {},
    programasDescargables: [],
    patrocinadores: [],
    sponsors: [],
    programaDetalladoSlots: [],
    programaPorDias: [],
    talleresPorDias: [],
    comunicacionesIntro: emptyComunicacionesIntro(),
    organizador: "actedi",
    inscripcionOrganizadorModo: "externa",
    inscripcionUrlExterna: "",
    redsysConfig: {
      merchantCode: "",
      terminal: "",
      secretKeyBase64: "",
      environment: "",
    },
    mailsInscripciones: [],
    politicaPrivacidadModo: "enlace",
    politicaPrivacidadUrl: {},
    politicaPrivacidadHtml: {},
    inscripcionSubtitulo: {},
    inscripcionesBloqueHtml: {},
    contactoMapsIframeSrc: "",
    ...getDefaultFooterFormState(),
    patrocinioOpcionesPdf: "",
  };
}

function emptyComunicacionesIntro() {
  return {
    titulo: {},
    subtitulo: {},
    normativaCard: {
      titulo: {},
      descripcion: {},
      textoPdf: {},
      normativaPdf: "",
      normativaPdfPorIdioma: { es: "", ca: "", en: "" },
    },
    envioCard: {
      titulo: {},
      descripcion: {},
      textoBoton: {},
      urlResumen: "",
    },
    premiosCard: {
      titulo: {},
      descripcion: {},
      textoDestacado: {},
    },
  };
}

const CONGRESO_INTRO_PDF_LANGS = new Set(["es", "ca", "en"]);

/** Rutas PDF normativa por idioma; legacy `normativaPdf` cuenta como castellano si falta es. */
function getNormativaPdfPorIdiomaFromCard(nc) {
  const o = { es: "", ca: "", en: "" };
  if (!nc || typeof nc !== "object") return o;
  const prev = nc.normativaPdfPorIdioma;
  if (prev && typeof prev === "object") {
    o.es = String(prev.es || "").trim();
    o.ca = String(prev.ca || "").trim();
    o.en = String(prev.en || "").trim();
  }
  const legacy = nc.normativaPdf != null ? String(nc.normativaPdf).trim() : "";
  if (legacy && !o.es) o.es = legacy;
  return o;
}

function mergeComunicacionesIntro(raw) {
  const e = emptyComunicacionesIntro();
  if (!raw || typeof raw !== "object") return e;
  const norm = { ...e.normativaCard, ...(raw.normativaCard || {}) };
  norm.titulo = { ...e.normativaCard.titulo, ...(norm.titulo || {}) };
  norm.descripcion = { ...e.normativaCard.descripcion, ...(norm.descripcion || {}) };
  norm.textoPdf = { ...e.normativaCard.textoPdf, ...(norm.textoPdf || {}) };
  const pdfNorm = getNormativaPdfPorIdiomaFromCard(norm);
  norm.normativaPdfPorIdioma = pdfNorm;
  norm.normativaPdf = pdfNorm.es;
  const env = { ...e.envioCard, ...(raw.envioCard || {}) };
  env.titulo = { ...e.envioCard.titulo, ...(env.titulo || {}) };
  env.descripcion = { ...e.envioCard.descripcion, ...(env.descripcion || {}) };
  env.textoBoton = { ...e.envioCard.textoBoton, ...(env.textoBoton || {}) };
  env.urlResumen = env.urlResumen != null ? String(env.urlResumen) : "";
  const pre = { ...e.premiosCard, ...(raw.premiosCard || {}) };
  pre.titulo = { ...e.premiosCard.titulo, ...(pre.titulo || {}) };
  pre.descripcion = { ...e.premiosCard.descripcion, ...(pre.descripcion || {}) };
  pre.textoDestacado = { ...e.premiosCard.textoDestacado, ...(pre.textoDestacado || {}) };
  return {
    titulo: { ...e.titulo, ...(raw.titulo || {}) },
    subtitulo: { ...e.subtitulo, ...(raw.subtitulo || {}) },
    normativaCard: norm,
    envioCard: env,
    premiosCard: pre,
  };
}

/** Iconos de tarjetas «Comunicaciones» (mismos trazos que webcongresos). */
function ComIntroSectionIcon({ type }) {
  const common = {
    className: "congreso-com-intro-card__svg",
    viewBox: "0 0 24 24",
    "aria-hidden": true,
    width: 40,
    height: 40,
  };
  if (type === "send") {
    return (
      <svg {...common}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
        />
      </svg>
    );
  }
  if (type === "award") {
    return (
      <svg {...common}>
        <path
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12z M8.21 13.89L7 23l5-3 5 3-1.21-9.12"
        />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <path
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6z M14 2v6h6 M16 13H8 M16 17H8 M10 9H8"
      />
    </svg>
  );
}

/** Convierte { es, ca, en } o legacy a un solo string (backoffice sin multiidioma en estos campos). */
function coerceI18nObjectToString(val) {
  if (val == null) return "";
  if (typeof val === "string") return String(val).trim();
  if (typeof val === "object") {
    return String(val.es || val.ca || val.en || "").trim();
  }
  return String(val).trim();
}

function copySedeI18nField(val) {
  if (val && typeof val === "object" && !Array.isArray(val)) {
    return {
      es: val.es != null ? String(val.es) : "",
      ca: val.ca != null ? String(val.ca) : "",
      en: val.en != null ? String(val.en) : "",
    };
  }
  const legacy = val != null ? String(val).trim() : "";
  return legacy ? { es: legacy } : {};
}

function emptySedeBloque() {
  return {
    titulo: {},
    descripcion: {},
    direccion: "",
    direccion2: "",
    webUrl: "",
    webUrl2: "",
    imagen: "",
    comoLlegarUrl: "",
    textoBoton: {},
  };
}

function mergeSedeBloque(raw) {
  const e = emptySedeBloque();
  if (!raw || typeof raw !== "object") return e;
  return {
    titulo: copySedeI18nField(raw.titulo),
    descripcion: copySedeI18nField(raw.descripcion),
    direccion: coerceI18nObjectToString(raw.direccion),
    direccion2: coerceI18nObjectToString(raw.direccion2),
    webUrl: raw.webUrl != null ? String(raw.webUrl).trim() : "",
    webUrl2: raw.webUrl2 != null ? String(raw.webUrl2).trim() : "",
    imagen: raw.imagen != null ? String(raw.imagen).trim() : "",
    comoLlegarUrl: raw.comoLlegarUrl != null ? String(raw.comoLlegarUrl).trim() : "",
    textoBoton: { ...e.textoBoton, ...(raw.textoBoton && typeof raw.textoBoton === "object" ? raw.textoBoton : {}) },
  };
}

function normalizePrecioTextoPlan(raw) {
  if (raw == null) return "";
  if (typeof raw === "string") return String(raw).trim();
  if (typeof raw === "object") {
    const o = raw;
    const pick = (k) => (o[k] != null ? String(o[k]).trim() : "");
    return pick("es") || pick("ca") || pick("en") || "";
  }
  return "";
}

function generatePlanId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `plan_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
}

function normalizePrecioImporte(raw) {
  if (raw == null || raw === "") return 0;
  const n = Number(String(raw).replace(",", "."));
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(n * 100) / 100;
}

/** Texto mostrado en web/listados a partir del importe (una sola fuente de verdad con precioImporte). */
function precioTextoDesdeImporte(importe) {
  const n = normalizePrecioImporte(importe);
  if (n <= 0) return "";
  try {
    return new Intl.NumberFormat("es-ES", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: Number.isInteger(n) ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(n);
  } catch {
    const s = Number.isInteger(n) ? String(n) : String(n).replace(".", ",");
    return `${s} €`;
  }
}

function emptyInscripcionPlan() {
  return {
    planId: "",
    recomendado: false,
    titulo: {},
    precioTexto: "",
    precioTextoAnterior: "",
    precioImporte: 0,
    inscripcionDesde: "",
    inscripcionHasta: "",
    perfilesPermitidos: [],
    caracteristicas: [{ texto: {} }],
  };
}

const PERFILES_INSCRIPCION = [
  "no_socio",
  "estudiante_no_socio",
  "socio_actedi",
  "estudiante",
];

const INSCRIPCION_VENTANA_YMD = /^\d{4}-\d{2}-\d{2}$/;

function normalizeYmdInscripcionVentana(v) {
  if (v == null) return "";
  const s = String(v).trim();
  if (!s) return "";
  if (INSCRIPCION_VENTANA_YMD.test(s)) return s;
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function mergeVentanaInscripcionPlan(rawPlan) {
  let desde = normalizeYmdInscripcionVentana(rawPlan.inscripcionDesde);
  let hasta = normalizeYmdInscripcionVentana(rawPlan.inscripcionHasta);
  if (desde && hasta && desde > hasta) {
    const t = desde;
    desde = hasta;
    hasta = t;
  }
  return { inscripcionDesde: desde, inscripcionHasta: hasta };
}

function normalizePerfilesPermitidosPlan(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  for (const x of raw) {
    const k = String(x);
    if (PERFILES_INSCRIPCION.includes(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

function mergeInscripcionPlanes(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((p) => {
    const vent = mergeVentanaInscripcionPlan(p);
    return {
      planId:
        p.planId != null && String(p.planId).trim() !== ""
          ? String(p.planId).trim()
          : generatePlanId(),
      recomendado: !!p.recomendado,
      titulo: { ...(p.titulo || {}) },
      precioTexto: normalizePrecioTextoPlan(p.precioTexto),
      precioTextoAnterior:
        p.precioTextoAnterior != null ? String(p.precioTextoAnterior).trim() : "",
      precioImporte: normalizePrecioImporte(
        p.precioImporte != null ? p.precioImporte : p.precioTexto
      ),
      inscripcionDesde: vent.inscripcionDesde,
      inscripcionHasta: vent.inscripcionHasta,
      perfilesPermitidos: normalizePerfilesPermitidosPlan(p.perfilesPermitidos),
      caracteristicas: Array.isArray(p.caracteristicas)
        ? p.caracteristicas.map((c) => ({
            texto: { ...(c && c.texto ? c.texto : {}) },
          }))
        : [],
    };
  });
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function dayKeyFromIso(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
}

function formatDayLabelForMigration(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const wd = new Intl.DateTimeFormat("es-ES", { weekday: "long" }).format(d);
  const capitalized = wd.charAt(0).toUpperCase() + wd.slice(1);
  return `${capitalized} ${d.getDate()}`;
}

function timeFromSlotStart(iso) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

function migrateSlotsToProgramaPorDias(slots) {
  if (!Array.isArray(slots) || !slots.length) return [];
  const groups = new Map();
  for (const s of slots) {
    if (!s.start) continue;
    const startIso =
      typeof s.start === "string" ? s.start : new Date(s.start).toISOString();
    const dk = dayKeyFromIso(startIso);
    if (!dk) continue;
    if (!groups.has(dk)) {
      const label = formatDayLabelForMigration(startIso);
      groups.set(dk, {
        etiqueta: { es: label, ca: label, en: label },
        actividades: [],
      });
    }
    const g = groups.get(dk);
    g.actividades.push({
      hora: timeFromSlotStart(startIso),
      titulo: { ...(s.titulo || {}) },
      descripcion: { ...(s.descripcion || {}) },
      sala: s.sala || "",
      categoria: (() => {
        const raw = s.categoria != null ? String(s.categoria).trim() : "";
        if (!raw) return {};
        return { es: raw, ca: raw, en: raw };
      })(),
    });
  }
  return [...groups.entries()]
    .sort((a, b) => a[0].localeCompare(b[0]))
    .map(([, v]) => v);
}

/** Legacy: categoria era string; web y backoffice usan objeto { es, ca, en }. */
function normalizeRedesSocialesList(raw) {
  if (!Array.isArray(raw)) return [];
  return raw
    .map((r) => ({
      url: typeof r === "string" ? String(r).trim() : String(r?.url || "").trim(),
    }))
    .filter((r) => r.url);
}

function normalizeActividadCategoria(act) {
  if (!act || typeof act !== "object") return act;
  const c = act.categoria;
  if (c == null || c === "") return { ...act, categoria: {} };
  if (typeof c === "string") {
    const t = c.trim();
    return { ...act, categoria: t ? { es: t, ca: t, en: t } : {} };
  }
  return act;
}

function normalizeProgramaActividad(act) {
  const base = normalizeActividadCategoria(act) || {};
  return {
    ...base,
    ponente: base.ponente && typeof base.ponente === "object" ? { ...base.ponente } : {},
    redesSociales: normalizeRedesSocialesList(base.redesSociales),
    anchoGrilla: normalizeCongresoAnchoGrilla(base.anchoGrilla, 100),
  };
}

function normalizeProgramaPorDias(dias) {
  if (!Array.isArray(dias)) return [];
  return dias.map((dia) => ({
    ...dia,
    actividades: sortActividadesPorHora(
      (dia.actividades || []).map(normalizeProgramaActividad)
    ),
  }));
}

function normalizeProgramaDetalladoSlots(slots) {
  if (!Array.isArray(slots)) return [];
  return slots.map((s) => {
    const c = s.categoria;
    if (typeof c === "string") {
      const t = c.trim();
      return { ...s, categoria: t ? { es: t, ca: t, en: t } : {} };
    }
    if (c == null || c === "") return { ...s, categoria: {} };
    return s;
  });
}

function normalizeProgramaDescargablesOne(arr) {
  if (!Array.isArray(arr) || !arr.length) return [];
  const first = arr[0] || {};
  const legacyArch = first.archivo != null ? String(first.archivo).trim() : "";
  const ap =
    first.archivosPorIdioma && typeof first.archivosPorIdioma === "object"
      ? first.archivosPorIdioma
      : {};
  const es = String(ap.es || legacyArch || "").trim();
  const ca = String(ap.ca || "").trim();
  const en = String(ap.en || "").trim();
  return [
    {
      titulo: { ...(first.titulo || {}) },
      archivo: es || legacyArch,
      archivosPorIdioma: { es, ca, en },
    },
  ];
}

/** Franjas dentro de un día de talleres (orden + talleres) */
function normalizeTalleresFranjasArray(arr) {
  if (!Array.isArray(arr)) return [];
  const rows = arr.map((franja, fi) => {
    let orden = Number(franja?.orden);
    if (!Number.isFinite(orden)) orden = fi;
    const hora = franja?.hora != null ? String(franja.hora).trim() : "";
    const talleres = (franja?.talleres || []).map((t) => {
      const capRaw = t?.capacidad;
      let capacidad = null;
      if (capRaw != null && capRaw !== "") {
        const n = parseInt(String(capRaw), 10);
        if (Number.isFinite(n) && n >= 0) capacidad = n;
      }
      return {
        tallerId:
          t?.tallerId != null && String(t.tallerId).trim() !== ""
            ? String(t.tallerId).trim()
            : "",
        titulo: t?.titulo && typeof t.titulo === "object" ? { ...t.titulo } : {},
        ponente: t?.ponente && typeof t.ponente === "object" ? { ...t.ponente } : {},
        redesSociales: normalizeRedesSocialesList(t?.redesSociales),
        descripcion: t?.descripcion && typeof t.descripcion === "object" ? { ...t.descripcion } : {},
        sala: t?.sala != null ? String(t.sala).trim() : "",
        enlaceUrl: t?.enlaceUrl != null ? String(t.enlaceUrl).trim() : "",
        capacidad,
        anchoGrilla: normalizeCongresoAnchoGrilla(t?.anchoGrilla, 100),
      };
    });
    return { orden, hora, talleres };
  });
  return sortFranjasPorHora(rows);
}

function normalizeTalleresPorDias(dias) {
  if (!Array.isArray(dias)) return [];
  return dias.map((dia) => {
    const etiqueta = dia?.etiqueta && typeof dia.etiqueta === "object" ? { ...dia.etiqueta } : {};
    const franjas = normalizeTalleresFranjasArray(dia?.franjas || []);
    return { etiqueta, franjas };
  });
}

function normalizePatrocinadores(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((p) => ({
    nombre: coerceI18nObjectToString(p?.nombre),
    logo: p?.logo != null ? String(p.logo).trim() : "",
    url: p?.url != null ? String(p.url).trim() : "",
  }));
}

function normalizeI18nUrlTriplet(raw) {
  const o = raw && typeof raw === "object" ? raw : {};
  return {
    es: String(o.es || "").trim(),
    ca: String(o.ca || "").trim(),
    en: String(o.en || "").trim(),
  };
}

function normalizeSelloCongreso(raw) {
  const s = raw && typeof raw === "object" ? raw : {};
  return {
    descripcion: s.descripcion && typeof s.descripcion === "object" ? { ...s.descripcion } : {},
    logo: s.logo != null ? String(s.logo).trim() : "",
    url: s.url != null ? String(s.url).trim() : "",
  };
}

function normalizeInformacionGeneralBloques(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((b) => ({
    titulo: b?.titulo && typeof b.titulo === "object" ? { ...b.titulo } : {},
    contenidoHtml:
      b?.contenidoHtml && typeof b.contenidoHtml === "object" ? { ...b.contenidoHtml } : {},
  }));
}

function escapeHtmlGuiaPlain(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function normalizeGuiaItemsRows(arr) {
  if (!Array.isArray(arr)) return [];
  return arr.map((b) => {
    const titulo = b?.titulo && typeof b.titulo === "object" ? { ...b.titulo } : {};
    let contenidoHtml =
      b?.contenidoHtml && typeof b.contenidoHtml === "object" ? { ...b.contenidoHtml } : {};
    const texto = b?.texto && typeof b.texto === "object" ? b.texto : null;
    const hasCont = ["es", "ca", "en"].some((lang) => String(contenidoHtml[lang] || "").trim());
    if (texto && !hasCont) {
      ["es", "ca", "en"].forEach((lang) => {
        const raw = String(texto[lang] || "").trim();
        if (raw) {
          contenidoHtml[lang] = raw
            .split(/\n\n+/)
            .map((para) => `<p>${escapeHtmlGuiaPlain(para).replace(/\n/g, "<br/>")}</p>`)
            .join("");
        }
      });
    }
    return { titulo, contenidoHtml };
  });
}

function normalizeMailsInscripcionesList(raw) {
  if (!Array.isArray(raw)) return [];
  const out = [];
  const seen = new Set();
  for (const item of raw) {
    const email = String(item || "")
      .trim()
      .toLowerCase();
    if (!email || !email.includes("@") || email.length > 200) continue;
    if (seen.has(email)) continue;
    seen.add(email);
    out.push(email);
  }
  return out;
}

function normalizeRedsysConfig(raw) {
  const cfg = raw && typeof raw === "object" ? raw : {};
  const environmentRaw = String(cfg.environment || "").trim().toLowerCase();
  return {
    merchantCode: String(cfg.merchantCode || "").trim(),
    terminal: String(cfg.terminal || "").trim(),
    secretKeyBase64: String(cfg.secretKeyBase64 || "").trim(),
    environment:
      environmentRaw === "production"
        ? "production"
        : environmentRaw === "test"
          ? "test"
          : "",
  };
}

function mergeEdicionFromApi(data) {
  const base = { ...getEmptyEdicion(), ...data };
  if (!Array.isArray(base.comites)) base.comites = [];
  base.comites = normalizeComites(base.comites);
  if (!Array.isArray(base.programaPorDias)) base.programaPorDias = [];
  if (
    !base.programaPorDias.length &&
    Array.isArray(base.programaDetalladoSlots) &&
    base.programaDetalladoSlots.length
  ) {
    base.programaPorDias = migrateSlotsToProgramaPorDias(base.programaDetalladoSlots);
  }
  base.programaPorDias = normalizeProgramaPorDias(base.programaPorDias);
  if (Array.isArray(base.programaDetalladoSlots)) {
    base.programaDetalladoSlots = normalizeProgramaDetalladoSlots(base.programaDetalladoSlots);
  }
  base.comunicacionesIntro = mergeComunicacionesIntro(base.comunicacionesIntro);
  base.guiaModeradoresPlantillaUrl = normalizeI18nUrlTriplet(base.guiaModeradoresPlantillaUrl);
  base.guiaPonentesPlantillaUrl = normalizeI18nUrlTriplet(base.guiaPonentesPlantillaUrl);
  base.informacionGeneralBloques = normalizeInformacionGeneralBloques(base.informacionGeneralBloques);
  base.guiaBuenasPracticasItems = normalizeGuiaItemsRows(base.guiaBuenasPracticasItems);
  base.guiaModeradoresItems = normalizeGuiaItemsRows(base.guiaModeradoresItems);
  base.guiaNormasItems = normalizeGuiaItemsRows(base.guiaNormasItems);
  base.guiaPonentesItems = normalizeGuiaItemsRows(base.guiaPonentesItems);
  base.guiaEnvioTrabajosItems = normalizeGuiaItemsRows(base.guiaEnvioTrabajosItems);
  base.selloEtico = normalizeSelloCongreso(base.selloEtico);
  base.selloAcreditacion = normalizeSelloCongreso(base.selloAcreditacion);
  base.sedeBloque = mergeSedeBloque(base.sedeBloque);
  if (!base.organizacionIntroHtml || typeof base.organizacionIntroHtml !== "object") {
    base.organizacionIntroHtml = {};
  }
  base.posterPublicitario =
    base.posterPublicitario != null ? String(base.posterPublicitario).trim() : "";
  base.inscripcionPlanes = mergeInscripcionPlanes(base.inscripcionPlanes);
  base.programasDescargables = normalizeProgramaDescargablesOne(
    base.programasDescargables
  );
  if (!Array.isArray(base.talleresPorDias)) base.talleresPorDias = [];
  const legacyFranjas = Array.isArray(base.talleresPorFranjas) ? base.talleresPorFranjas : [];
  const talleresPorDiasHasContent = base.talleresPorDias.some((d) => {
    const lab = ["es", "ca", "en"].some((k) => String(d?.etiqueta?.[k] || "").trim());
    const fr = (d?.franjas || []).some((f) => String(f?.hora || "").trim() || (f?.talleres || []).length > 0);
    return lab || fr;
  });
  if (legacyFranjas.length && !talleresPorDiasHasContent) {
    base.talleresPorDias = [{ etiqueta: {}, franjas: normalizeTalleresFranjasArray(legacyFranjas) }];
  }
  delete base.talleresPorFranjas;
  base.talleresPorDias = normalizeTalleresPorDias(base.talleresPorDias);
  base.patrocinadores = normalizePatrocinadores(base.patrocinadores);
  base.sponsors = normalizePatrocinadores(base.sponsors);
  base.organizadores = normalizePatrocinadores(base.organizadores);
  if (!base.organizadores.length) base.organizadores = organizadoresPorDefecto();
  base.editionLogo = base.editionLogo != null ? String(base.editionLogo).trim() : "";
  base.cronogramaImage =
    base.cronogramaImage != null ? String(base.cronogramaImage).trim() : "";
  base.contactoEmail =
    base.contactoEmail != null && String(base.contactoEmail).trim()
      ? String(base.contactoEmail).trim()
      : "actedi@actedi.cat";
  base.titulo = coerceI18nObjectToString(base.titulo);
  const org = String(base.organizador || "actedi").toLowerCase();
  base.organizador = org === "otro" ? "otro" : "actedi";
  const modoRaw = String(base.inscripcionOrganizadorModo || "").toLowerCase();
  if (modoRaw === "externa" || modoRaw === "redsys") {
    base.inscripcionOrganizadorModo = modoRaw;
  } else {
    base.inscripcionOrganizadorModo = String(base.inscripcionUrlExterna || "").trim()
      ? "externa"
      : "redsys";
  }
  base.inscripcionUrlExterna =
    base.inscripcionUrlExterna != null ? String(base.inscripcionUrlExterna).trim() : "";
  base.redsysConfig = normalizeRedsysConfig(base.redsysConfig);
  base.mailsInscripciones = normalizeMailsInscripcionesList(base.mailsInscripciones);
  const privModo = String(base.politicaPrivacidadModo || "enlace").toLowerCase();
  base.politicaPrivacidadModo = privModo === "texto" ? "texto" : "enlace";
  base.politicaPrivacidadUrl =
    base.politicaPrivacidadUrl && typeof base.politicaPrivacidadUrl === "object"
      ? { ...base.politicaPrivacidadUrl }
      : {};
  if (!base.politicaPrivacidadHtml || typeof base.politicaPrivacidadHtml !== "object") {
    base.politicaPrivacidadHtml = {};
  }
  base.inscripcionSubtitulo =
    base.inscripcionSubtitulo && typeof base.inscripcionSubtitulo === "object"
      ? { ...base.inscripcionSubtitulo }
      : {};
  if (!base.inscripcionesBloqueHtml || typeof base.inscripcionesBloqueHtml !== "object") {
    base.inscripcionesBloqueHtml = {};
  }
  base.heroSede =
    base.heroSede && typeof base.heroSede === "object" ? { ...base.heroSede } : {};
  base.heroFechas =
    base.heroFechas && typeof base.heroFechas === "object" ? { ...base.heroFechas } : {};
  base.contactoMapsIframeSrc =
    base.contactoMapsIframeSrc != null ? String(base.contactoMapsIframeSrc).trim() : "";
  const copyFooterI18n = (key) =>
    base[key] && typeof base[key] === "object" ? { ...base[key] } : {};
  base.footerTituloEnlaces = copyFooterI18n("footerTituloEnlaces");
  base.footerTituloLegal = copyFooterI18n("footerTituloLegal");
  base.footerTituloContacto = copyFooterI18n("footerTituloContacto");
  base.footerEnlaceCongreso = copyFooterI18n("footerEnlaceCongreso");
  base.footerEnlaceContactoCongreso = copyFooterI18n("footerEnlaceContactoCongreso");
  base.footerEnlaceInscripcion = copyFooterI18n("footerEnlaceInscripcion");
  base.footerEnlaceActediWeb = copyFooterI18n("footerEnlaceActediWeb");
  base.footerEnlacesUtiles = normalizeFooterEnlacesList(base.footerEnlacesUtiles);
  base.footerEnlacesLegal = normalizeFooterEnlacesList(base.footerEnlacesLegal);
  base.footerTelefono = copyFooterI18n("footerTelefono");
  base.footerDireccion = copyFooterI18n("footerDireccion");
  base.footerSellosTitulo = copyFooterI18n("footerSellosTitulo");
  ensureFooterDefaultsInPlace(base);
  base.patrocinioOpcionesPdf =
    base.patrocinioOpcionesPdf != null ? String(base.patrocinioOpcionesPdf).trim() : "";
  delete base.inscripcionesHtml;
  delete base.myAccess;
  return base;
}

function edicionComparableForDirty(state) {
  if (!state || typeof state !== "object") return "";
  try {
    return JSON.stringify(state, (k, v) => {
      if (k === "myAccess") return undefined;
      return v;
    });
  } catch {
    return "";
  }
}

/** Datos de API → formulario de alta nueva copiando otra edición (sin _id; año sugerido +1). */
function prepareCloneForNewForm(apiData) {
  const merged = mergeEdicionFromApi({ ...apiData });
  delete merged._id;
  delete merged.__v;
  if (merged.createdAt != null) delete merged.createdAt;
  if (merged.updatedAt != null) delete merged.updatedAt;
  merged.isDefault = false;
  merged.published = false;
  merged.active = true;
  const y = Number(apiData.year);
  merged.year =
    Number.isInteger(y) && y >= 2000 && y < 2100 ? y + 1 : "";
  return merged;
}

function isOrganizadorOtroEdicion(edicion) {
  return String(edicion?.organizador || "actedi").toLowerCase() === "otro";
}

/** Modo de inscripción cuando el organizador es «Otro». */
function getInscripcionOrganizadorModoEdicion(edicion) {
  const raw = String(edicion?.inscripcionOrganizadorModo || "")
    .trim()
    .toLowerCase();
  if (raw === "redsys" || raw === "externa") return raw;
  return String(edicion?.inscripcionUrlExterna || "").trim() ? "externa" : "redsys";
}

/** ACTEDI o «Otro» + Redsys: planes y formulario integrado. */
function usaPlanesInscripcionIntegrados(edicion) {
  if (!isOrganizadorOtroEdicion(edicion)) return true;
  return getInscripcionOrganizadorModoEdicion(edicion) === "redsys";
}

const MAIN_TAB_GENERAL = 0;
const MAIN_TAB_CARTA = 1;
const MAIN_TAB_CRONOGRAMA = 2;
const MAIN_TAB_COMITES = 3;
const MAIN_TAB_PROGRAMA = 4;
const MAIN_TAB_TALLERES = 5;
const MAIN_TAB_COMUNICACION = 6;
const MAIN_TAB_GUIAS_NORMAS = 7;
const MAIN_TAB_SEDE = 8;
const MAIN_TAB_INSCRIPCION = 9;
const MAIN_TAB_PATROC = 10;
const MAIN_TAB_ORGANIZADOR = 11;
const MAIN_TAB_PRIVACIDAD = 12;

const APARTADOS_CONGRESO = [
  { id: MAIN_TAB_GENERAL, label: "General", Icon: DashboardOutlinedIcon },
  { id: MAIN_TAB_CARTA, label: "Carta de bienvenida", Icon: MailOutlineIcon },
  { id: MAIN_TAB_CRONOGRAMA, label: "Cronograma", Icon: ImageOutlinedIcon },
  { id: MAIN_TAB_COMITES, label: "Comités", Icon: GroupWorkOutlinedIcon },
  { id: MAIN_TAB_TALLERES, label: "Talleres", Icon: SchoolOutlinedIcon },
  { id: MAIN_TAB_PROGRAMA, label: "Programa científico", Icon: EventOutlinedIcon },
  {
    id: MAIN_TAB_COMUNICACION,
    label: "Comunicación y Pósters",
    Icon: ChatBubbleOutlineIcon,
  },
  {
    id: MAIN_TAB_GUIAS_NORMAS,
    label: "Guías y normas",
    Icon: SubjectOutlinedIcon,
  },
  { id: MAIN_TAB_SEDE, label: "Información General", Icon: PlaceOutlinedIcon },
  { id: MAIN_TAB_INSCRIPCION, label: "Inscripciones", Icon: HowToRegOutlinedIcon },
  { id: MAIN_TAB_PATROC, label: "Patrocinadores", Icon: BusinessOutlinedIcon },
  {
    id: MAIN_TAB_ORGANIZADOR,
    label: "Organizador",
    Icon: SupervisorAccountOutlinedIcon,
  },
];

const TAB_ID_TO_SECTION = {
  [MAIN_TAB_GENERAL]: "general",
  [MAIN_TAB_CARTA]: "carta",
  [MAIN_TAB_CRONOGRAMA]: "cronograma",
  [MAIN_TAB_COMITES]: "comites",
  [MAIN_TAB_PROGRAMA]: "programa",
  [MAIN_TAB_TALLERES]: "talleres",
  [MAIN_TAB_COMUNICACION]: "comunicacion",
  [MAIN_TAB_GUIAS_NORMAS]: "comunicacion",
  [MAIN_TAB_SEDE]: "sede",
  [MAIN_TAB_INSCRIPCION]: "inscripcion",
  [MAIN_TAB_PATROC]: "patrocinadores",
  [MAIN_TAB_ORGANIZADOR]: "organizador",
  [MAIN_TAB_PRIVACIDAD]: "privacidad",
};

export default function EditCongresoEdicionPage() {
  const [edicion, setEdicion] = useState(() => getEmptyEdicion({ conComitesPorDefecto: true }));
  const [mainTab, setMainTab] = useState(MAIN_TAB_GENERAL);
  const [mailAvisoDraft, setMailAvisoDraft] = useState("");
  const imageInputRef = useRef(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openPreviewDialog, setOpenPreviewDialog] = useState(null);
  const [openShareDialog, setOpenShareDialog] = useState(false);
  const [myAccess, setMyAccess] = useState(null);
  const edicionId = useParams().id;
  const history = useHistory();
  const location = useLocation();
  const cloneSeedDoneRef = useRef(false);
  const saveBaselineRef = useRef(null);
  const user = useSelector((store) => store.authentication?.user, shallowEqual);

  const delegadoFormLocked =
    isCongresoDelegado(user?.role) && myAccess?.accessLevel === "read";

  const delegadoTabDisabled = (tabId) => {
    if (!isCongresoDelegado(user?.role) || !myAccess) return false;
    if (myAccess.accessLevel !== "sections") return false;
    const key = TAB_ID_TO_SECTION[tabId];
    if (!key) return false;
    const allowed = myAccess.allowedSectionKeys || [];
    return !allowed.includes(key);
  };

  useEffect(() => {
    saveBaselineRef.current = null;
  }, [edicionId]);

  const hasUnsavedEdicion = useMemo(() => {
    if (delegadoFormLocked) return false;
    if (selectedImage) return true;
    const baseline = saveBaselineRef.current;
    if (baseline === null) return false;
    return edicionComparableForDirty(edicion) !== baseline;
  }, [edicion, selectedImage, delegadoFormLocked]);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    if (isCongresoDelegado(user?.role) && !edicionId) {
      const plantilla = new URLSearchParams(location.search).get("plantilla");
      if (!plantilla) {
        history.replace("/congresos");
      }
    }
  }, [user?.role, edicionId, location.search, history]);

  useEffect(() => {
    if (edicionId) {
      cloneSeedDoneRef.current = false;
      getCongresoEdicionById(edicionId)
        .then((res) => {
          if (res.status === 200) {
            const merged = mergeEdicionFromApi(res.data);
            setEdicion(merged);
            saveBaselineRef.current = edicionComparableForDirty(merged);
            setMyAccess(res.data.myAccess || null);
            disableLoadingData();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudo obtener la edición de congreso.",
          });
          history.push("/congresos");
        });
      return;
    }

    const plantilla = new URLSearchParams(location.search).get("plantilla");
    if (plantilla) {
      getCongresoEdicionById(plantilla)
        .then((res) => {
          if (res.status === 200) {
            const cloned = prepareCloneForNewForm(res.data);
            setEdicion(cloned);
            saveBaselineRef.current = edicionComparableForDirty(cloned);
            setSelectedImage(null);
            setMyAccess(null);
            cloneSeedDoneRef.current = true;
            history.replace("/edit-congreso-edicion");
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "No se pudo cargar la edición plantilla.",
          });
          setEdicion(getEmptyEdicion({ conComitesPorDefecto: true }));
          saveBaselineRef.current = edicionComparableForDirty(
            getEmptyEdicion({ conComitesPorDefecto: true }),
          );
          cloneSeedDoneRef.current = false;
          history.replace("/edit-congreso-edicion");
        })
        .finally(() => disableLoadingData());
      return;
    }

    if (!cloneSeedDoneRef.current) {
      const empty = getEmptyEdicion({ conComitesPorDefecto: true });
      setEdicion(empty);
      saveBaselineRef.current = edicionComparableForDirty(empty);
      setSelectedImage(null);
      setMyAccess(null);
    }
    disableLoadingData();
  }, [edicionId, location.search, history, disableLoadingData]);

  function addMailInscripcionAviso() {
    const parts = String(mailAvisoDraft || "")
      .split(";")
      .map((p) => p.trim().toLowerCase())
      .filter(Boolean);
    if (!parts.length) {
      alertError({ customMessage: "Introduce uno o más emails separados por ;" });
      return;
    }
    const invalid = parts.filter((e) => !e.includes("@"));
    if (invalid.length) {
      alertError({
        customMessage: `Email(s) no válido(s): ${invalid.join(", ")}`,
      });
      return;
    }
    const current = normalizeMailsInscripcionesList(edicion.mailsInscripciones);
    const toAdd = [];
    const seen = new Set(current);
    for (const email of parts) {
      if (seen.has(email)) continue;
      seen.add(email);
      toAdd.push(email);
    }
    if (!toAdd.length) {
      alertError({ customMessage: "Esos emails ya están en la lista." });
      return;
    }
    setEdicion({
      ...edicion,
      mailsInscripciones: [...current, ...toAdd],
    });
    setMailAvisoDraft("");
  }

  function removeMailInscripcionAviso(mail) {
    const current = normalizeMailsInscripcionesList(edicion.mailsInscripciones);
    setEdicion({
      ...edicion,
      mailsInscripciones: current.filter((m) => m !== mail),
    });
  }

  function saveEdicion() {
    if (isCongresoDelegado(user?.role) && (!myAccess || !myAccess.canEdit)) {
      alertError({
        customMessage: "No tienes permiso de edición en esta edición.",
      });
      return;
    }

    const payload = { ...edicion };
    delete payload.myAccess;
    if (String(payload.organizador || "actedi").toLowerCase() === "otro") {
      payload.organizador = "otro";
      payload.inscripcionOrganizadorModo =
        String(payload.inscripcionOrganizadorModo || "").toLowerCase() === "redsys"
          ? "redsys"
          : "externa";
      payload.redsysConfig = normalizeRedsysConfig(payload.redsysConfig);
      if (payload.inscripcionOrganizadorModo === "redsys") {
        payload.inscripcionUrlExterna = "";
        // Mismos planes / perfiles que con inscripción ACTEDI.
      } else {
        payload.redsysConfig = normalizeRedsysConfig({});
        payload.inscripcionPlanes = (payload.inscripcionPlanes || []).map((p) => ({
          ...p,
          perfilesPermitidos: [],
        }));
      }
    } else {
      payload.organizador = "actedi";
      payload.inscripcionOrganizadorModo = "redsys";
      payload.redsysConfig = normalizeRedsysConfig({});
      payload.inscripcionUrlExterna = "";
      if (payload.comunicacionesIntro && payload.comunicacionesIntro.envioCard) {
        payload.comunicacionesIntro = {
          ...payload.comunicacionesIntro,
          envioCard: {
            ...payload.comunicacionesIntro.envioCard,
            urlResumen: "",
          },
        };
      }
    }
    payload.mailsInscripciones = normalizeMailsInscripcionesList(
      payload.mailsInscripciones
    );
    payload.politicaPrivacidadModo =
      String(payload.politicaPrivacidadModo || "enlace").toLowerCase() === "texto"
        ? "texto"
        : "enlace";
    payload.politicaPrivacidadUrl =
      payload.politicaPrivacidadUrl && typeof payload.politicaPrivacidadUrl === "object"
        ? { ...payload.politicaPrivacidadUrl }
        : {};
    if (
      !payload.politicaPrivacidadHtml ||
      typeof payload.politicaPrivacidadHtml !== "object"
    ) {
      payload.politicaPrivacidadHtml = {};
    }
    payload.programasDescargables = normalizeProgramaDescargablesOne(
      payload.programasDescargables
    );
    payload.programaPorDias = normalizeProgramaPorDias(payload.programaPorDias || []);
    payload.talleresPorDias = normalizeTalleresPorDias(payload.talleresPorDias || []);
    if (Array.isArray(payload.programaPorDias) && payload.programaPorDias.length > 0) {
      payload.programaDetalladoSlots = [];
    }
    if (!edicionId && !payload.year) {
      alertError({ customMessage: "El año es obligatorio." });
      return;
    }
    payload.footerEnlacesUtiles = normalizeFooterEnlacesList(payload.footerEnlacesUtiles).filter(
      footerEnlaceRowHasContent
    );
    payload.footerEnlacesLegal = normalizeFooterEnlacesList(payload.footerEnlacesLegal).filter(
      footerEnlaceRowHasContent
    );

    const request = edicionId
      ? updateCongresoEdicion(edicionId, payload, selectedImage)
      : postCongresoEdicion(payload, selectedImage);

    request
      .then((res) => {
        if (res.status === 200 || res.status === 201) {
          alertSuccess({
            title: "Guardado!",
            customMessage: edicionId
              ? "Cambios guardados correctamente."
              : "Edición de congreso creada correctamente.",
          });
          history.push("/congresos");
        }
      })
      .catch((error) => {
        alertError({
          error: error,
          customMessage: "No se pudo guardar la edición de congreso.",
        });
      });
  }

  const handleChange = (element, lang) => (event) => {
    if (lang) {
      if (event.target.value === " ") return;
      const current = edicion[element] || {};
      setEdicion({
        ...edicion,
        [element]: { ...current, [lang]: event.target.value },
      });
    } else {
      setEdicion({ ...edicion, [element]: event.target.value });
    }
  };

  const handleChangeEditor = (element, lang, value) => {
    if (lang) {
      if (value === " ") return;
      const current = edicion[element] || {};
      setEdicion({
        ...edicion,
        [element]: { ...current, [lang]: value },
      });
    } else {
      setEdicion({ ...edicion, [element]: value });
    }
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    showDelete = false,
    showShare = false,
    onOpenShare,
    readOnlySave = false,
    hasUnsavedChanges = false,
  }) => (
    <div
      className="sticky-top bg-white border-top shadow-sm"
      style={{
        /* Metronic: header fijo 97, aside 98, subheader 95 — por debajo para no tapar dropdowns del topbar */
        zIndex: 96,
        paddingLeft: "15px",
        paddingRight: "15px",
      }}
    >
      <div className="container-fluid">
        <div className="row py-3">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center flex-wrap">
              <div className="d-flex" style={{ gap: "12px" }}>
                <Button
                  onClick={onBack}
                  variant="outlined"
                  size="medium"
                  className="text-nowrap"
                  startIcon={<ArrowBackIcon />}
                >
                  Volver
                </Button>
              </div>
              <div
                className="d-flex align-items-center flex-wrap"
                style={{ gap: "20px" }}
              >
                {showShare && (
                  <Button
                    onClick={onOpenShare}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ShareIcon />}
                    style={{
                      borderColor: "#f3691a",
                      color: "#f3691a",
                      minWidth: 120,
                    }}
                  >
                    Compartir
                  </Button>
                )}
                {readOnlySave ? (
                  <Typography
                    variant="body1"
                    color="textSecondary"
                    className="text-nowrap"
                    style={{
                      minWidth: 140,
                      fontWeight: 600,
                      paddingLeft: 12,
                      paddingRight: 12,
                    }}
                  >
                    Solo lectura
                  </Typography>
                ) : (
                  <Button
                    onClick={onSave}
                    variant={hasUnsavedChanges ? "contained" : "outlined"}
                    size="medium"
                    className="text-nowrap"
                    startIcon={<SaveOutlinedIcon />}
                    style={
                      hasUnsavedChanges
                        ? {
                            backgroundColor: "#f3691a",
                            color: "#fff",
                            minWidth: 140,
                            fontWeight: 600,
                            paddingLeft: 20,
                            paddingRight: 20,
                            boxShadow: "0 4px 14px rgba(243, 105, 26, 0.45)",
                          }
                        : {
                            borderColor: "#f3691a",
                            color: "#f3691a",
                            minWidth: 140,
                            fontWeight: 600,
                            paddingLeft: 20,
                            paddingRight: 20,
                          }
                    }
                  >
                    Guardar
                  </Button>
                )}
                {showDelete && (
                  <MuiThemeProvider theme={theme}>
                    <Button
                      onClick={onDelete}
                      variant="outlined"
                      color="secondary"
                      size="medium"
                      className="text-nowrap"
                      style={{ minWidth: 100 }}
                    >
                      Eliminar
                    </Button>
                  </MuiThemeProvider>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const comitesList = () => edicion.comites || [];

  const addComite = () =>
    setEdicion({
      ...edicion,
      comites: [...comitesList(), { nombre: {}, miembros: [], anchoGrilla: 50 }],
    });

  const removeComite = (ci) => {
    const n = [...comitesList()];
    n.splice(ci, 1);
    setEdicion({ ...edicion, comites: n });
  };

  const updateComiteNombre = (ci, lang, val) => {
    const n = [...comitesList()];
    const prev = n[ci] || { nombre: {}, miembros: [], anchoGrilla: 50 };
    n[ci] = {
      ...prev,
      nombre: { ...(prev.nombre || {}), [lang]: val },
    };
    setEdicion({ ...edicion, comites: n });
  };

  const updateComiteAnchoGrilla = (ci, ancho) => {
    const n = [...comitesList()];
    const prev = n[ci] || { nombre: {}, miembros: [], anchoGrilla: 50 };
    const v = normalizeCongresoAnchoGrilla(ancho, 50);
    n[ci] = { ...prev, anchoGrilla: v };
    setEdicion({ ...edicion, comites: n });
  };

  const addMiembro = (ci) => {
    const n = [...comitesList()];
    const c = n[ci] || { nombre: {}, miembros: [], anchoGrilla: 50 };
    n[ci] = {
      ...c,
      miembros: [...(c.miembros || []), { texto: {}, foto: "", redesSociales: [] }],
    };
    setEdicion({ ...edicion, comites: n });
  };

  const removeMiembro = (ci, mi) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    m.splice(mi, 1);
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const updateMiembroTexto = (ci, mi, lang, val) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
    m[mi] = { ...row, texto: { ...(row.texto || {}), [lang]: val } };
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const setMiembroFoto = async (ci, mi, file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const url = res?.data?.fileURL;
      const n = [...comitesList()];
      const c = n[ci];
      if (!c) return;
      const m = [...(c.miembros || [])];
      const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
      m[mi] = { ...row, foto: url || row.foto };
      n[ci] = { ...c, miembros: m };
      setEdicion({ ...edicion, comites: n });
    } catch (e) {
      alertError({ error: e, customMessage: "No se pudo subir la imagen." });
    }
  };

  const clearMiembroFoto = (ci, mi) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
    m[mi] = { ...row, foto: "" };
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const addMiembroRed = (ci, mi) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
    const reds = [...(row.redesSociales || []), { url: "" }];
    m[mi] = { ...row, redesSociales: reds };
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const updateMiembroRedUrl = (ci, mi, ri, val) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
    const reds = [...(row.redesSociales || [])];
    const prev = reds[ri] || { url: "" };
    reds[ri] = { ...prev, url: val };
    m[mi] = { ...row, redesSociales: reds };
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const removeMiembroRed = (ci, mi, ri) => {
    const n = [...comitesList()];
    const c = n[ci];
    if (!c) return;
    const m = [...(c.miembros || [])];
    const row = m[mi] || { texto: {}, foto: "", redesSociales: [] };
    const reds = [...(row.redesSociales || [])];
    reds.splice(ri, 1);
    m[mi] = { ...row, redesSociales: reds };
    n[ci] = { ...c, miembros: m };
    setEdicion({ ...edicion, comites: n });
  };

  const introMerged = () => mergeComunicacionesIntro(edicion.comunicacionesIntro);

  const patchIntroI18n = (field, lang, val) => {
    const cur = introMerged();
    cur[field] = { ...(cur[field] || {}), [lang]: val };
    setEdicion({ ...edicion, comunicacionesIntro: cur });
  };

  const patchIntroCardI18n = (cardKey, field, lang, val) => {
    const cur = introMerged();
    const card = { ...(cur[cardKey] || {}) };
    card[field] = { ...(card[field] || {}), [lang]: val };
    cur[cardKey] = card;
    setEdicion({ ...edicion, comunicacionesIntro: cur });
  };

  const patchIntroCardStr = (cardKey, field, val) => {
    const cur = introMerged();
    const card = { ...(cur[cardKey] || {}) };
    card[field] = val;
    cur[cardKey] = card;
    setEdicion({ ...edicion, comunicacionesIntro: cur });
  };

  const uploadNormativaPdfLang = async (lang, file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const url = res?.data?.fileURL;
      const cur = introMerged();
      const nc = { ...(cur.normativaCard || {}) };
      const ap = getNormativaPdfPorIdiomaFromCard(nc);
      ap[lang] = (url || "").trim() || ap[lang];
      nc.normativaPdfPorIdioma = { es: ap.es, ca: ap.ca, en: ap.en };
      nc.normativaPdf = nc.normativaPdfPorIdioma.es;
      cur.normativaCard = nc;
      setEdicion({ ...edicion, comunicacionesIntro: cur });
    } catch (e) {
      alertError({ error: e, customMessage: "No se pudo subir el PDF de normativa." });
    }
  };

  const clearNormativaPdfLang = (lang) => {
    const cur = introMerged();
    const nc = { ...(cur.normativaCard || {}) };
    const ap = getNormativaPdfPorIdiomaFromCard(nc);
    ap[lang] = "";
    nc.normativaPdfPorIdioma = { es: ap.es, ca: ap.ca, en: ap.en };
    nc.normativaPdf = nc.normativaPdfPorIdioma.es;
    cur.normativaCard = nc;
    setEdicion({ ...edicion, comunicacionesIntro: cur });
  };

  const patchGuiaPlantillaUrl = (field, lang, val) => {
    const cur = normalizeI18nUrlTriplet(edicion[field]);
    setEdicion({
      ...edicion,
      [field]: { ...cur, [lang]: val },
    });
  };

  const patchSelloDescripcion = (which, lang, val) => {
    const s = normalizeSelloCongreso(edicion[which]);
    setEdicion({
      ...edicion,
      [which]: {
        ...s,
        descripcion: { ...(s.descripcion || {}), [lang]: val },
      },
    });
  };

  const patchSelloUrlStr = (which, val) => {
    const s = normalizeSelloCongreso(edicion[which]);
    setEdicion({ ...edicion, [which]: { ...s, url: val } });
  };

  const patchSelloLogoStr = (which, path) => {
    const s = normalizeSelloCongreso(edicion[which]);
    setEdicion({ ...edicion, [which]: { ...s, logo: path } });
  };

  const uploadSelloLogo = async (which, file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const url = res?.data?.fileURL;
      patchSelloLogoStr(which, (url || "").trim());
    } catch (e) {
      alertError({ error: e, customMessage: "No se pudo subir el logo del sello." });
    }
  };

  const addGuiaItem = (field) => {
    const list = [...(edicion[field] || [])];
    list.push({ titulo: {}, contenidoHtml: {} });
    setEdicion({ ...edicion, [field]: list });
  };

  const removeGuiaItem = (field, idx) => {
    const list = [...(edicion[field] || [])];
    list.splice(idx, 1);
    setEdicion({ ...edicion, [field]: list });
  };

  const moveGuiaItem = (field, idx, delta) => {
    const list = [...(edicion[field] || [])];
    const j = idx + delta;
    if (j < 0 || j >= list.length) return;
    const tmp = list[j];
    list[j] = list[idx];
    list[idx] = tmp;
    setEdicion({ ...edicion, [field]: list });
  };

  const patchGuiaItemI18n = (field, idx, subkey, lang, val) => {
    const list = [...(edicion[field] || [])];
    const row = { ...(list[idx] || { titulo: {}, contenidoHtml: {} }) };
    row[subkey] = { ...(row[subkey] || {}), [lang]: val };
    list[idx] = row;
    setEdicion({ ...edicion, [field]: list });
  };

  const sedeMerged = () => mergeSedeBloque(edicion.sedeBloque);

  const patchSedeStr = (field, val) => {
    const cur = sedeMerged();
    cur[field] = val;
    setEdicion({ ...edicion, sedeBloque: cur });
  };

  const patchSedeTextoBotonLang = (lang, val) => {
    const cur = sedeMerged();
    cur.textoBoton = { ...(cur.textoBoton || {}), [lang]: val };
    setEdicion({ ...edicion, sedeBloque: cur });
  };

  const patchSedeI18nLang = (field, lang, val) => {
    const cur = sedeMerged();
    const prev =
      cur[field] && typeof cur[field] === "object" && !Array.isArray(cur[field])
        ? { ...cur[field] }
        : {};
    prev[lang] = val;
    cur[field] = prev;
    setEdicion({ ...edicion, sedeBloque: cur });
  };

  const renderSedeTitleDescMultilang = () => (
    <MultilanguageTabBlock
      contentDisabled={delegadoFormLocked}
      multilanguageTabContent={(lang) => {
        const sb = sedeMerged();
        return (
          <>
            <TextField
              label="Título"
              fullWidth
              variant="outlined"
              margin="dense"
              value={(sb.titulo && sb.titulo[lang]) || ""}
              onChange={(e) => patchSedeI18nLang("titulo", lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Descripción"
              fullWidth
              variant="outlined"
              margin="dense"
              multiline
              minRows={3}
              className="mt-2"
              value={(sb.descripcion && sb.descripcion[lang]) || ""}
              onChange={(e) => patchSedeI18nLang("descripcion", lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </>
        );
      }}
    />
  );

  const renderSedeUnilingual = () => {
    const sb = sedeMerged();
    return (
      <>
        <TextField
          label="Dirección"
          fullWidth
          variant="outlined"
          margin="dense"
          value={sb.direccion || ""}
          onChange={(e) => patchSedeStr("direccion", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="Dirección (segunda sede u hotel, opcional)"
          fullWidth
          variant="outlined"
          margin="dense"
          multiline
          minRows={2}
          value={sb.direccion2 || ""}
          onChange={(e) => patchSedeStr("direccion2", e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </>
    );
  };

  const setInscripcionPlanes = (planes) =>
    setEdicion({ ...edicion, inscripcionPlanes: mergeInscripcionPlanes(planes) });

  const updatePlanScalarField = (index, field, value) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const next = { ...list[index], [field]: value };
    if (field === "precioImporte") {
      next.precioTexto = precioTextoDesdeImporte(value);
    }
    list[index] = next;
    setInscripcionPlanes(list);
  };

  const updatePlanI18n = (index, field, lang, val) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const row = { ...list[index] };
    row[field] = { ...(row[field] || {}), [lang]: val };
    list[index] = row;
    setInscripcionPlanes(list);
  };

  const updatePlanFeatureText = (planIdx, featIdx, lang, val) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const row = { ...list[planIdx] };
    const feats = [...(row.caracteristicas || [])];
    const prev = feats[featIdx] || { texto: {} };
    feats[featIdx] = {
      texto: { ...(prev.texto || {}), [lang]: val },
    };
    row.caracteristicas = feats;
    list[planIdx] = row;
    setInscripcionPlanes(list);
  };

  const addPlanFeature = (planIdx) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const row = { ...list[planIdx] };
    row.caracteristicas = [...(row.caracteristicas || []), { texto: {} }];
    list[planIdx] = row;
    setInscripcionPlanes(list);
  };

  const removePlanFeature = (planIdx, featIdx) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const row = { ...list[planIdx] };
    const feats = [...(row.caracteristicas || [])];
    feats.splice(featIdx, 1);
    row.caracteristicas = feats.length ? feats : [{ texto: {} }];
    list[planIdx] = row;
    setInscripcionPlanes(list);
  };

  const addInscripcionPlan = () => {
    setInscripcionPlanes([...(edicion.inscripcionPlanes || []), emptyInscripcionPlan()]);
  };

  const removeInscripcionPlan = (index) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    list.splice(index, 1);
    setInscripcionPlanes(list);
  };

  const togglePlanRecomendado = (index) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    list[index] = { ...list[index], recomendado: !list[index].recomendado };
    setInscripcionPlanes(list);
  };

  const togglePlanPerfilPermitido = (planIndex, perfilKey) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const row = { ...list[planIndex] };
    const cur = normalizePerfilesPermitidosPlan(row.perfilesPermitidos);
    const i = cur.indexOf(perfilKey);
    if (i >= 0) cur.splice(i, 1);
    else cur.push(perfilKey);
    row.perfilesPermitidos = cur;
    list[planIndex] = row;
    setInscripcionPlanes(list);
  };

  const renderInscripcionPlanLangFields = (planIndex, lang) => {
    const list = mergeInscripcionPlanes(edicion.inscripcionPlanes || []);
    const plan = list[planIndex] || emptyInscripcionPlan();
    const feats = plan.caracteristicas || [];
    return (
      <>
        <TextField
          label="Nombre del plan"
          fullWidth
          variant="outlined"
          margin="dense"
          value={(plan.titulo && plan.titulo[lang]) || ""}
          onChange={(e) => updatePlanI18n(planIndex, "titulo", lang, e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <h6 className="mt-2 mb-1">Incluye:</h6>
        {feats.map((f, fi) => (
          <div key={fi} className="d-flex align-items-start mb-1">
            <TextField
              label={`Línea ${fi + 1}`}
              fullWidth
              variant="outlined"
              margin="dense"
              value={(f.texto && f.texto[lang]) || ""}
              onChange={(e) => updatePlanFeatureText(planIndex, fi, lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <IconButton
              size="small"
              aria-label="Quitar línea"
              onClick={() => removePlanFeature(planIndex, fi)}
              className="mt-1"
            >
              <Delete fontSize="small" />
            </IconButton>
          </div>
        ))}
        <Button size="small" variant="outlined" onClick={() => addPlanFeature(planIndex)}>
          Añadir línea
        </Button>
      </>
    );
  };

  const renderComunicacionIntroLang = (lang) => {
    const intro = introMerged();
    const nc = intro.normativaCard;
    const ec = intro.envioCard;
    const pc = intro.premiosCard;
    const soloEs = lang === "es";
    const apn = getNormativaPdfPorIdiomaFromCard(nc);
    const pathNormPdf = CONGRESO_INTRO_PDF_LANGS.has(lang) ? String(apn[lang] || "").trim() : "";
    return (
      <div className="congreso-com-intro-editor">
        <div className="congreso-com-intro-editor__lead">
          <TextField
            label="Subtítulo"
            fullWidth
            variant="outlined"
            margin="dense"
            multiline
            minRows={2}
            value={(intro.subtitulo && intro.subtitulo[lang]) || ""}
            onChange={(e) => patchIntroI18n("subtitulo", lang, e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </div>

        <div className="congreso-com-intro-editor__grid">
          <div className="congreso-com-intro-card">
            <div className="congreso-com-intro-card__head">
              <ComIntroSectionIcon type="doc" />
              <h6 className="congreso-com-intro-card__title">Normativa</h6>
            </div>
            <div className="congreso-com-intro-card__body">
              <TextField
                label="Descripción"
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                minRows={3}
                value={(nc.descripcion && nc.descripcion[lang]) || ""}
                onChange={(e) =>
                  patchIntroCardI18n("normativaCard", "descripcion", lang, e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              {CONGRESO_INTRO_PDF_LANGS.has(lang) ? (
                <div className="mt-2">
                  <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
                    PDF de normativa para este idioma. En la web, si no hay PDF en catalán o inglés, se usará el de
                    castellano cuando exista.
                  </Typography>
                  <div className="d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
                    {pathNormPdf ? (
                      <>
                        <a
                          href={`${SERVER_URL}/${pathNormPdf}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="congreso-edicion-programa-pdf-link"
                        >
                          Ver PDF
                        </a>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => clearNormativaPdfLang(lang)}
                          className="congreso-edicion-btn-orange"
                        >
                          Quitar PDF
                        </Button>
                      </>
                    ) : null}
                    <label className="mb-0">
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) uploadNormativaPdfLang(lang, f);
                          e.target.value = "";
                        }}
                      />
                      <Button variant="outlined" size="small" component="span" className="congreso-edicion-btn-orange">
                        Subir PDF
                      </Button>
                    </label>
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className="congreso-com-intro-card">
            <div className="congreso-com-intro-card__head">
              <ComIntroSectionIcon type="send" />
              <h6 className="congreso-com-intro-card__title">Envío de trabajos</h6>
            </div>
            <div className="congreso-com-intro-card__body">
              <TextField
                label="Descripción"
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                minRows={3}
                value={(ec.descripcion && ec.descripcion[lang]) || ""}
                onChange={(e) =>
                  patchIntroCardI18n("envioCard", "descripcion", lang, e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              {soloEs && String(edicion.organizador || "actedi").toLowerCase() === "otro" ? (
                <TextField
                  className="mt-2"
                  label="URL del envío de resúmenes"
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  placeholder="https://…"
                  value={ec.urlResumen || ""}
                  onChange={(e) =>
                    patchIntroCardStr("envioCard", "urlResumen", e.target.value)
                  }
                  InputLabelProps={{ shrink: true }}
                  helperText="Solo aplica cuando el organizador de la edición es «Otro»."
                />
              ) : null}
              {soloEs && String(edicion.organizador || "actedi").toLowerCase() !== "otro" ? (
                <Typography variant="body2" color="textSecondary" className="mt-2">
                  La web usará el formulario integrado de envío de trabajos (organizador ACTEDI).
                </Typography>
              ) : null}
            </div>
          </div>

          <div className="congreso-com-intro-card">
            <div className="congreso-com-intro-card__head">
              <ComIntroSectionIcon type="award" />
              <h6 className="congreso-com-intro-card__title">Premios</h6>
            </div>
            <div className="congreso-com-intro-card__body">
              <TextField
                label="Descripción"
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                minRows={3}
                value={(pc.descripcion && pc.descripcion[lang]) || ""}
                onChange={(e) =>
                  patchIntroCardI18n("premiosCard", "descripcion", lang, e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                className="mt-2"
                label="Texto destacado"
                fullWidth
                variant="outlined"
                margin="dense"
                multiline
                minRows={2}
                value={(pc.textoDestacado && pc.textoDestacado[lang]) || ""}
                onChange={(e) =>
                  patchIntroCardI18n("premiosCard", "textoDestacado", lang, e.target.value)
                }
                InputLabelProps={{ shrink: true }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderGuiasNormasItemsEditors = () => {
    const macro = ({
      title,
      hint,
      introHtmlField,
      itemsField,
      plantillaField,
      plantillaLabel,
      plantillaHelper,
    }) => {
      const list = edicion[itemsField] || [];
      return (
        <section className="congreso-guia-macro" key={itemsField}>
          <header className="congreso-guia-macro__head">
            <Typography variant="h5" component="h3" className="congreso-guia-macro__title">
              {title}
            </Typography>
            <Typography variant="body1" color="textSecondary" className="congreso-guia-macro__hint">
              {hint}
            </Typography>
          </header>
          <div className="congreso-guia-macro__intro-editor">
            <Typography variant="subtitle1" className="congreso-guia-macro__subblock-title">
              Texto principal (siempre visible al abrir el apartado, sin acordeón)
            </Typography>
            <Typography variant="body2" color="textSecondary" className="mb-2">
              Editor de texto enriquecido. Debajo puede añadir acordeones para el resto del contenido.
            </Typography>
            <MultilanguageTabBlock
              contentDisabled={delegadoFormLocked}
              multilanguageTabContent={(lang) => (
                <Editor
                  body={(edicion[introHtmlField] && edicion[introHtmlField][lang]) || ""}
                  setBody={(v) => handleChangeEditor(introHtmlField, lang, v)}
                  className="max-height"
                  lang={lang}
                />
              )}
            />
          </div>
          {plantillaField ? (
            <div className="congreso-guia-macro__plantilla">
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={(lang) => (
                  <TextField
                    label={plantillaLabel}
                    fullWidth
                    variant="outlined"
                    margin="normal"
                    value={(normalizeI18nUrlTriplet(edicion[plantillaField])[lang] || "")}
                    onChange={(e) => patchGuiaPlantillaUrl(plantillaField, lang, e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    helperText={plantillaHelper}
                  />
                )}
              />
            </div>
          ) : null}
          <Typography variant="subtitle1" className="congreso-guia-macro__subblock-title mt-3">
            Acordeones dentro de este apartado
          </Typography>
          <div className="congreso-guia-macro__toolbar">
            <Button
              variant="contained"
              className="congreso-edicion-btn-orange"
              startIcon={<AddIcon />}
              disabled={delegadoFormLocked}
              onClick={() => addGuiaItem(itemsField)}
            >
              Añadir acordeón
            </Button>
          </div>
          {list.map((_, idx) => {
            const row = (edicion[itemsField] || [])[idx] || { titulo: {}, contenidoHtml: {} };
            return (
              <CardMaterial key={idx} className="congreso-guia-acordeon-card" variant="outlined">
                <CardBody>
                  <div className="d-flex justify-content-between align-items-start flex-wrap mb-2">
                    <Typography variant="h6" className="congreso-guia-acordeon-card__num">
                      Acordeón {idx + 1}
                    </Typography>
                    <div className="d-flex align-items-center" style={{ gap: 2 }}>
                      <Tooltip title="Mover arriba">
                        <span>
                          <IconButton
                            size="small"
                            disabled={delegadoFormLocked || idx === 0}
                            onClick={() => moveGuiaItem(itemsField, idx, -1)}
                          >
                            <KeyboardArrowUpIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Mover abajo">
                        <span>
                          <IconButton
                            size="small"
                            disabled={delegadoFormLocked || idx >= list.length - 1}
                            onClick={() => moveGuiaItem(itemsField, idx, 1)}
                          >
                            <KeyboardArrowDownIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Eliminar este acordeón">
                        <IconButton
                          size="small"
                          disabled={delegadoFormLocked}
                          onClick={() => removeGuiaItem(itemsField, idx)}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </Tooltip>
                    </div>
                  </div>
                  <MultilanguageTabBlock
                    contentDisabled={delegadoFormLocked}
                    multilanguageTabContent={(lang) => (
                      <>
                        <TextField
                          label="Título del acordeón (visible en la web)"
                          fullWidth
                          variant="outlined"
                          margin="normal"
                          value={(row.titulo && row.titulo[lang]) || ""}
                          onChange={(e) =>
                            patchGuiaItemI18n(itemsField, idx, "titulo", lang, e.target.value)
                          }
                          InputLabelProps={{ shrink: true }}
                          inputProps={{ style: { fontSize: "1.05rem", fontWeight: 600 } }}
                        />
                        <Typography variant="caption" color="textSecondary" display="block" className="mb-1">
                          Contenido del acordeón (editor)
                        </Typography>
                        <Editor
                          body={(row.contenidoHtml && row.contenidoHtml[lang]) || ""}
                          setBody={(v) =>
                            patchGuiaItemI18n(itemsField, idx, "contenidoHtml", lang, v)
                          }
                          className="max-height"
                          lang={lang}
                        />
                      </>
                    )}
                  />
                </CardBody>
              </CardMaterial>
            );
          })}
        </section>
      );
    };

    return (
      <div className="congreso-guia-paneles-root">
        {macro({
          title: "Código de buenas prácticas",
          hint: "Texto fijo arriba + acordeones opcionales (p. ej. «Introducción», capítulos…).",
          introHtmlField: "guiaBuenasPracticasHtml",
          itemsField: "guiaBuenasPracticasItems",
        })}
        {macro({
          title: "Moderadores",
          hint: "Texto principal + acordeones + enlace opcional a plantilla.",
          introHtmlField: "guiaModeradoresHtml",
          itemsField: "guiaModeradoresItems",
          plantillaField: "guiaModeradoresPlantillaUrl",
          plantillaLabel: "URL plantilla o modelo (PDF o enlace, por pestaña de idioma)",
          plantillaHelper:
            "Opcional. Aparece como botón «Descargar plantilla» bajo este bloque en la web.",
        })}
        {macro({
          title: "Normas",
          hint: "Texto principal + acordeones. El PDF de la tarjeta «Comunicaciones» se sube en la otra pestaña.",
          introHtmlField: "normativaHtml",
          itemsField: "guiaNormasItems",
        })}
        {macro({
          title: "Ponentes",
          hint: "Texto principal + acordeones + plantilla de presentaciones opcional.",
          introHtmlField: "guiaPonentesHtml",
          itemsField: "guiaPonentesItems",
          plantillaField: "guiaPonentesPlantillaUrl",
          plantillaLabel: "URL plantilla de presentaciones (por idioma)",
          plantillaHelper: "Opcional. Botón de descarga bajo este bloque en la web.",
        })}
        {macro({
          title: "Normativa de envío de trabajos",
          hint: "Texto principal + acordeones. El acceso al formulario de envío depende del organizador de la edición.",
          introHtmlField: "envioResumenHtml",
          itemsField: "guiaEnvioTrabajosItems",
        })}
      </div>
    );
  };

  const footerEnlacesList = (listKey) => normalizeFooterEnlacesList(edicion[listKey] || []);

  const addFooterEnlace = (listKey) => {
    const list = [...footerEnlacesList(listKey), { etiqueta: {}, url: footerUrlI18nSame("", "") }];
    setEdicion({ ...edicion, [listKey]: list });
  };

  const removeFooterEnlace = (listKey, index) => {
    const list = footerEnlacesList(listKey).filter((_, i) => i !== index);
    setEdicion({ ...edicion, [listKey]: list });
  };

  const patchFooterEnlaceEtiqueta = (listKey, index, lang, val) => {
    const list = [...footerEnlacesList(listKey)];
    const row = { ...(list[index] || { etiqueta: {}, url: footerUrlI18nSame("", "") }) };
    const etiqueta = { ...(row.etiqueta || {}), [lang]: val };
    list[index] = { ...row, etiqueta };
    setEdicion({ ...edicion, [listKey]: list });
  };

  const patchFooterEnlaceUrl = (listKey, index, lang, val) => {
    const list = [...footerEnlacesList(listKey)];
    const row = { ...(list[index] || { etiqueta: {}, url: footerUrlI18nSame("", "") }) };
    const url = { ...normalizeFooterUrlField(row.url), [lang]: val };
    list[index] = { ...row, url };
    setEdicion({ ...edicion, [listKey]: list });
  };

  const resetFooterEnlacesUtiles = () => {
    const y = Number(edicion.year);
    const yearForLinks =
      Number.isInteger(y) && y >= 2000 && y < 2100 ? y : new Date().getFullYear();
    setEdicion({ ...edicion, footerEnlacesUtiles: buildDefaultFooterEnlacesUtiles(yearForLinks) });
  };

  const resetFooterEnlacesLegal = () => {
    setEdicion({ ...edicion, footerEnlacesLegal: buildDefaultFooterEnlacesLegal() });
  };

  const renderFooterEnlacesCard = (lang, listKey, titleField, titleLabel, onReset) => (
    <div className="congreso-edicion-comite-card" style={{ gridColumn: "span 4" }}>
      <TextField
        label={titleLabel}
        fullWidth
        variant="outlined"
        margin="dense"
        value={(edicion[titleField] && edicion[titleField][lang]) || ""}
        onChange={handleChange(titleField, lang)}
        InputLabelProps={{ shrink: true }}
      />
      <div className="congreso-edicion-comite-miembro-redes mt-2">
        <Typography
          variant="caption"
          color="textSecondary"
          component="p"
          className="congreso-edicion-comite-miembro-redes__caption"
        >
          Enlaces (texto y URL). Rutas internas: <code>{`/{lang}/{year}`}</code>; externos: https://…
        </Typography>
        {footerEnlacesList(listKey).map((row, index) => (
          <div key={`${listKey}-${index}`} className="congreso-footer-enlace-row">
            <TextField
              label="Texto del enlace"
              size="small"
              fullWidth
              variant="outlined"
              margin="dense"
              value={(row.etiqueta && row.etiqueta[lang]) || ""}
              onChange={(e) => patchFooterEnlaceEtiqueta(listKey, index, lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="URL"
              size="small"
              fullWidth
              variant="outlined"
              margin="dense"
              value={(row.url && row.url[lang]) || ""}
              onChange={(e) => patchFooterEnlaceUrl(listKey, index, lang, e.target.value)}
              placeholder={lang === "es" ? "/{lang}/2026/contacto" : undefined}
              InputLabelProps={{ shrink: true }}
            />
            <Tooltip title="Quitar enlace">
              <IconButton
                size="small"
                onClick={() => removeFooterEnlace(listKey, index)}
                aria-label={`Quitar enlace ${index + 1}`}
                disabled={delegadoFormLocked}
                className="congreso-edicion-comite-miembro-redes__del"
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        ))}
        <div className="d-flex flex-wrap align-items-center" style={{ gap: "0.5rem" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => addFooterEnlace(listKey)}
            disabled={delegadoFormLocked}
            className="congreso-edicion-btn-orange congreso-edicion-comite-miembro-redes__add"
          >
            Añadir enlace
          </Button>
          {onReset ? (
            <Button size="small" onClick={onReset} disabled={delegadoFormLocked}>
              Restaurar por defecto
            </Button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const renderSelloFooterCard = (lang, selloKey, label) => {
    const s = normalizeSelloCongreso(edicion[selloKey]);
    return (
      <div className="congreso-edicion-comite-card" style={{ gridColumn: "span 6" }}>
        <Typography variant="subtitle2" className="mb-2" style={{ fontWeight: 700 }}>
          {label}
        </Typography>
        <TextField
          label="Descripción"
          fullWidth
          variant="outlined"
          margin="dense"
          multiline
          minRows={2}
          value={(s.descripcion && s.descripcion[lang]) || ""}
          onChange={(e) => patchSelloDescripcion(selloKey, lang, e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          className="mt-2"
          label="URL"
          fullWidth
          variant="outlined"
          margin="dense"
          value={s.url || ""}
          onChange={(e) => patchSelloUrlStr(selloKey, e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <div className="mt-2 d-flex align-items-center flex-wrap" style={{ gap: "0.35rem" }}>
          {s.logo ? (
            <>
              <CardMedia
                component="img"
                alt=""
                style={{ maxWidth: 140, maxHeight: 56, objectFit: "contain" }}
                image={`${SERVER_URL}/${s.logo}`}
              />
              <Button
                size="small"
                onClick={() => patchSelloLogoStr(selloKey, "")}
                disabled={delegadoFormLocked}
              >
                Quitar logo
              </Button>
            </>
          ) : null}
          <label>
            <input
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              disabled={delegadoFormLocked}
              onChange={(e) => {
                const f = e.target.files && e.target.files[0];
                if (f) uploadSelloLogo(selloKey, f);
                e.target.value = "";
              }}
            />
            <Button
              variant="outlined"
              size="small"
              component="span"
              disabled={delegadoFormLocked}
              className="congreso-edicion-btn-orange"
            >
              Subir logo
            </Button>
          </label>
        </div>
      </div>
    );
  };

  const renderFooterForLang = (lang) => (
    <div className="congreso-edicion-comites-panel">
      <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
        Tres columnas del pie de la web. El email de contacto se edita arriba en «Email de contacto».
      </Typography>
      <div className="congreso-edicion-comites-grid">
        {renderFooterEnlacesCard(
          lang,
          "footerEnlacesUtiles",
          "footerTituloEnlaces",
          "Título columna · Enlaces útiles",
          resetFooterEnlacesUtiles
        )}
        {renderFooterEnlacesCard(
          lang,
          "footerEnlacesLegal",
          "footerTituloLegal",
          "Título columna · Legal",
          resetFooterEnlacesLegal
        )}
        <div className="congreso-edicion-comite-card" style={{ gridColumn: "span 4" }}>
          <TextField
            label="Título columna · Contacto"
            fullWidth
            variant="outlined"
            margin="dense"
            value={(edicion.footerTituloContacto && edicion.footerTituloContacto[lang]) || ""}
            onChange={handleChange("footerTituloContacto", lang)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Teléfono (texto mostrado)"
            fullWidth
            variant="outlined"
            margin="dense"
            className="mt-2"
            value={(edicion.footerTelefono && edicion.footerTelefono[lang]) || ""}
            onChange={handleChange("footerTelefono", lang)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Dirección (texto mostrado)"
            fullWidth
            variant="outlined"
            margin="dense"
            className="mt-2"
            value={(edicion.footerDireccion && edicion.footerDireccion[lang]) || ""}
            onChange={handleChange("footerDireccion", lang)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Teléfono para enlace (solo dígitos)"
            fullWidth
            variant="outlined"
            margin="dense"
            className="mt-2"
            value={edicion.footerTelefonoE164 || ""}
            onChange={(e) =>
              setEdicion({
                ...edicion,
                footerTelefonoE164: String(e.target.value || "").replace(/\D/g, ""),
              })
            }
            placeholder="34630380886"
            InputLabelProps={{ shrink: true }}
            disabled={delegadoFormLocked}
          />
          <TextField
            label="Búsqueda Google Maps (opcional)"
            fullWidth
            variant="outlined"
            margin="dense"
            className="mt-2"
            value={edicion.footerMapsBusqueda || ""}
            onChange={(e) => setEdicion({ ...edicion, footerMapsBusqueda: e.target.value })}
            placeholder="Si difiere de la dirección mostrada"
            InputLabelProps={{ shrink: true }}
            disabled={delegadoFormLocked}
          />
        </div>
      </div>
      <div className="congreso-edicion-comites-grid mt-3">
        <div className="congreso-edicion-comite-card" style={{ gridColumn: "span 12" }}>
          <TextField
            label="Título bloque · Sellos y acreditaciones"
            fullWidth
            variant="outlined"
            margin="dense"
            value={(edicion.footerSellosTitulo && edicion.footerSellosTitulo[lang]) || ""}
            onChange={handleChange("footerSellosTitulo", lang)}
            InputLabelProps={{ shrink: true }}
          />
        </div>
        {renderSelloFooterCard(lang, "selloEtico", "Sello ético (p. ej. FEETS)")}
        {renderSelloFooterCard(lang, "selloAcreditacion", "Sello de acreditación")}
      </div>
    </div>
  );

  const ensureProgramaDescargableList = () => {
    const list = [...(edicion.programasDescargables || [])];
    if (!list.length) {
      list.push({
        titulo: {},
        archivo: "",
        archivosPorIdioma: { es: "", ca: "", en: "" },
      });
    }
    const row = { ...list[0] };
    if (!row.archivosPorIdioma || typeof row.archivosPorIdioma !== "object") {
      row.archivosPorIdioma = { es: "", ca: "", en: "" };
    }
    row.archivosPorIdioma = {
      es: String(row.archivosPorIdioma.es || "").trim(),
      ca: String(row.archivosPorIdioma.ca || "").trim(),
      en: String(row.archivosPorIdioma.en || "").trim(),
    };
    if (row.archivo && !row.archivosPorIdioma.es) {
      row.archivosPorIdioma.es = String(row.archivo).trim();
    }
    list[0] = row;
    return list;
  };

  const setProgramaArchivoLang = async (lang, file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const url = res?.data?.fileURL;
      const list = ensureProgramaDescargableList();
      const row = { ...list[0] };
      const ap = { ...(row.archivosPorIdioma || {}) };
      ap[lang] = url || ap[lang] || "";
      row.archivosPorIdioma = ap;
      row.archivo = String(ap.es || row.archivo || "").trim();
      list[0] = row;
      setEdicion({ ...edicion, programasDescargables: [list[0]] });
    } catch (e) {
      alertError({ error: e, customMessage: "No se pudo subir el archivo." });
    }
  };

  const clearProgramaArchivoLang = (lang) => {
    const list = ensureProgramaDescargableList();
    const row = { ...list[0] };
    const ap = { ...(row.archivosPorIdioma || {}) };
    ap[lang] = "";
    row.archivosPorIdioma = ap;
    row.archivo = String(ap.es || "").trim();
    list[0] = row;
    setEdicion({ ...edicion, programasDescargables: [list[0]] });
  };

  const programaPdfLangs = new Set(["es", "ca", "en"]);

  const renderGeneralLangFields = (lang) => {
    const listPd = ensureProgramaDescargableList();
    const rowPd = listPd[0] || {};
    const ap = rowPd.archivosPorIdioma || { es: "", ca: "", en: "" };
    const pathPdf = programaPdfLangs.has(lang) ? String(ap[lang] || "").trim() : "";

    return (
      <>
        <TextField
          id={`heroBadge-${lang}`}
          label="badge superior"
          value={(edicion.heroBadge && edicion.heroBadge[lang]) || ""}
          onChange={handleChange("heroBadge", lang)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />
        <TextField
          id={`heroSubtitle-${lang}`}
          label="Subtítulo del congreso"
          value={(edicion.heroSubtitle && edicion.heroSubtitle[lang]) || ""}
          onChange={handleChange("heroSubtitle", lang)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
        />
        <TextField
          id={`heroSede-${lang}`}
          label="Sede"
          value={(edicion.heroSede && edicion.heroSede[lang]) || ""}
          onChange={handleChange("heroSede", lang)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
          helperText="Opcional. Se muestra en la portada de la web del congreso (debajo del subtítulo)."
        />
        <TextField
          id={`heroFechas-${lang}`}
          label="Fechas"
          value={(edicion.heroFechas && edicion.heroFechas[lang]) || ""}
          onChange={handleChange("heroFechas", lang)}
          InputLabelProps={{ shrink: true }}
          margin="normal"
          variant="outlined"
          fullWidth
          helperText="Opcional. Ej. «23–25 de mayo de 2026». Portada web del congreso."
        />
        {programaPdfLangs.has(lang) ? (
          <div className="congreso-edicion-programa-pdf-card">
            <div className="congreso-edicion-programa-pdf-card__head">
              <PictureAsPdfIcon className="congreso-edicion-programa-pdf-card__icon" aria-hidden />
              <div>
                <Typography variant="subtitle1" component="h3" className="mb-1" style={{ fontWeight: 700 }}>
                  Programa descargable (PDF)
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p">
                  PDF para este idioma. En la web, si no hay PDF en catalán o inglés, se usará el de
                  castellano cuando exista.
                </Typography>
              </div>
            </div>
            <div className="congreso-edicion-btn-row">
              <label className="mb-0">
                <input
                  type="file"
                  accept="application/pdf,.pdf"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) setProgramaArchivoLang(lang, f);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outlined"
                  component="span"
                  size="small"
                  startIcon={<CloudUploadOutlinedIcon />}
                  className="congreso-edicion-btn-orange"
                >
                  Subir PDF
                </Button>
              </label>
              {pathPdf ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DeleteOutlineIcon />}
                    onClick={() => clearProgramaArchivoLang(lang)}
                    className="congreso-edicion-btn-orange"
                  >
                    Eliminar PDF
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    component="a"
                    href={`${SERVER_URL}/${pathPdf}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    startIcon={<PictureAsPdfIcon />}
                    className="congreso-edicion-btn-orange"
                  >
                    Ver PDF
                  </Button>
                </>
              ) : null}
            </div>
          </div>
        ) : null}
      </>
    );
  };

  const renderComitesForLang = (lang) => (
    <div className="congreso-edicion-comites-panel">
      <div className="congreso-edicion-comites-grid">
        {comitesList().map((comite, ci) => (
          <div
            key={ci}
            className="congreso-edicion-comite-card"
            style={{ gridColumn: `span ${anchoGrillaToSpan12(comite.anchoGrilla)}` }}
          >
            <div className="d-flex justify-content-end mb-2">
              <IconButton size="small" onClick={() => removeComite(ci)} aria-label="Quitar">
                <Delete fontSize="small" />
              </IconButton>
            </div>
            <TextField
              label="Comité"
              fullWidth
              variant="outlined"
              margin="dense"
              value={(comite.nombre && comite.nombre[lang]) || ""}
              onChange={(e) => updateComiteNombre(ci, lang, e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="caption" color="textSecondary" display="block" className="mt-2 mb-1">
              Ancho en la web de congresos
            </Typography>
            <div className="congreso-edicion-comite-ancho-rail" role="radiogroup" aria-label="Ancho del comité">
              {[
                { v: 33, short: "33%", hint: "Ocupa un tercio de la fila (p. ej. junto a otro más ancho)" },
                { v: 50, short: "MITAD DE ANCHO", hint: "Mitad de la fila: dos comités del mismo tamaño" },
                { v: 66, short: "66%", hint: "Dos tercios de la fila" },
                { v: 100, short: "ANCHO COMPLETO", hint: "Toda la fila; otro comité pasará debajo si no cabe" },
              ].map(({ v, short, hint }) => {
                const active = Number(comite.anchoGrilla ?? 50) === v;
                return (
                  <Tooltip key={v} title={hint}>
                    <button
                      type="button"
                      className={`congreso-edicion-comite-ancho-seg${active ? " is-selected" : ""}`}
                      aria-pressed={active}
                      onClick={() => updateComiteAnchoGrilla(ci, v)}
                    >
                      <span className="congreso-edicion-comite-ancho-seg__bar-wrap" aria-hidden>
                        <span
                          className={`congreso-edicion-comite-ancho-bar congreso-edicion-comite-ancho-bar--a${v}`}
                        />
                      </span>
                      <span className="congreso-edicion-comite-ancho-seg__label">{short}</span>
                    </button>
                  </Tooltip>
                );
              })}
            </div>
            <div
              className={
                "mt-2 congreso-edicion-comite-miembros" +
                ([66, 100].includes(Number(comite.anchoGrilla ?? 50))
                  ? " congreso-edicion-comite-miembros--2col"
                  : "")
              }
            >
              {(comite.miembros || []).map((miembro, mi) => (
                <div
                  key={mi}
                  className="congreso-edicion-comite-miembro-card"
                >
                  <div className="congreso-edicion-comite-miembro-card__header">
                    <Tooltip title="Quitar este miembro del comité">
                      <IconButton
                        size="small"
                        onClick={() => removeMiembro(ci, mi)}
                        aria-label="Quitar miembro"
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                  <div className="congreso-edicion-comite-miembro-card__body">
                    <div className="congreso-edicion-comite-miembro-card__main">
                      <TextField
                        label="Texto"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        multiline
                        minRows={2}
                        value={(miembro.texto && miembro.texto[lang]) || ""}
                        onChange={(e) =>
                          updateMiembroTexto(ci, mi, lang, e.target.value)
                        }
                        InputLabelProps={{ shrink: true }}
                      />
                      <div className="congreso-edicion-comite-miembro-redes">
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          component="p"
                          className="congreso-edicion-comite-miembro-redes__caption"
                        >
                          Redes sociales (URL; en la web se elige el icono según el enlace)
                        </Typography>
                        {(miembro.redesSociales || []).map((red, ri) => (
                          <div key={ri} className="congreso-edicion-comite-miembro-redes__row">
                            <TextField
                              label="URL"
                              size="small"
                              fullWidth
                              variant="outlined"
                              margin="dense"
                              placeholder="https://linkedin.com/in/…"
                              value={red.url || ""}
                              onChange={(e) => updateMiembroRedUrl(ci, mi, ri, e.target.value)}
                              InputLabelProps={{ shrink: true }}
                            />
                            <Tooltip title="Quitar este enlace">
                              <IconButton
                                size="small"
                                onClick={() => removeMiembroRed(ci, mi, ri)}
                                aria-label="Quitar enlace"
                                className="congreso-edicion-comite-miembro-redes__del"
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </div>
                        ))}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => addMiembroRed(ci, mi)}
                          className="congreso-edicion-btn-orange congreso-edicion-comite-miembro-redes__add"
                        >
                          Añadir red
                        </Button>
                      </div>
                    </div>
                    <div className="congreso-edicion-comite-miembro-card__photo-col">
                      <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
                        Foto (opcional) — tamaño recomendado: <strong>{IMG_TAM_CONGRESO.comiteMiembro}</strong>
                      </Typography>
                      <div className="congreso-edicion-comite-miembro-photo">
                        {miembro.foto ? (
                          <img
                            src={`${SERVER_URL}/${miembro.foto}`}
                            alt=""
                            className="congreso-edicion-comite-miembro-photo__avatar"
                          />
                        ) : (
                          <div
                            className="congreso-edicion-comite-miembro-photo__placeholder"
                            aria-hidden
                          >
                            <PhotoCameraOutlinedIcon style={{ fontSize: 28 }} />
                          </div>
                        )}
                        <div className="congreso-edicion-comite-miembro-photo__actions">
                          <label className="mb-0">
                            <input
                              type="file"
                              accept="image/*"
                              style={{ display: "none" }}
                              onChange={(e) =>
                                setMiembroFoto(
                                  ci,
                                  mi,
                                  e.target.files && e.target.files[0]
                                )
                              }
                            />
                            <Button
                              variant="outlined"
                              size="small"
                              component="span"
                              className="congreso-edicion-btn-orange"
                            >
                              Foto
                            </Button>
                          </label>
                          {miembro.foto ? (
                            <Tooltip title="Quitar foto">
                              <IconButton
                                size="small"
                                onClick={() => clearMiembroFoto(ci, mi)}
                                aria-label="Quitar foto"
                              >
                                <DeleteOutlineIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <Button
                size="small"
                variant="outlined"
                onClick={() => addMiembro(ci)}
                className="congreso-edicion-btn-orange congreso-edicion-comite-miembros__add"
              >
                Miembro
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={addComite}
        className="mt-2 congreso-edicion-btn-orange"
      >
        Comité
      </Button>
    </div>
  );

  const navBgPickerValue = isHex6(edicion.themeNavBackground)
    ? edicion.themeNavBackground.trim()
    : "#00033c";
  const navAccentPickerValue = isHex6(edicion.themeNavAccent)
    ? edicion.themeNavAccent.trim()
    : "#22d3ee";
  const accentSecondaryPickerValue = isHex6(edicion.themeAccentSecondary)
    ? edicion.themeAccentSecondary.trim()
    : "#0891b2";

  const renderGeneralFields = () => {
    return (
      <>
        <GeneralApartado Icon={EditOutlinedIcon} title="Edición del congreso">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="d-flex align-text-center flex-wrap">
              <div className="col-md-6">
                {!edicionId ? (
                  <TextField
                    id="year"
                    label="Año de publicación"
                    type="number"
                    value={edicion.year}
                    onChange={(e) =>
                      setEdicion({
                        ...edicion,
                        year: parseInt(e.target.value, 10) || "",
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    required
                    fullWidth
                  />
                ) : (
                  <TextField
                    id="year-readonly"
                    label="Año de publicación"
                    value={edicion.year}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    disabled
                    fullWidth
                  />
                )}
              </div>
              <div className="col-md-12">
                <TextField
                  id="titulo-congreso"
                  label="Título del congreso"
                  value={
                    typeof edicion.titulo === "string"
                      ? edicion.titulo
                      : coerceI18nObjectToString(edicion.titulo)
                  }
                  onChange={(e) => setEdicion({ ...edicion, titulo: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                />
              </div>
              <div className="col-md-12 mt-3">
                <Typography variant="subtitle1" component="h3" className="mt-1 mb-1" style={{ fontWeight: 700 }}>
                  Organizador
                </Typography>
                <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
                  Quién gestiona la inscripción en esta web.
                </Typography>
                <div className="congreso-org-tiles" role="radiogroup" aria-label="Organizador del congreso">
                  <button
                    type="button"
                    role="radio"
                    aria-checked={
                      String(edicion.organizador || "actedi").toLowerCase() !== "otro"
                    }
                    className={
                      "congreso-org-tile" +
                      (String(edicion.organizador || "actedi").toLowerCase() !== "otro"
                        ? " congreso-org-tile--selected"
                        : "")
                    }
                    onClick={() => setEdicion({ ...edicion, organizador: "actedi" })}
                  >
                    <span className="congreso-org-tile__mark" aria-hidden>
                      ACTEDI
                    </span>
                    <span className="congreso-org-tile__label">Inscripción ACTEDI</span>
                    <span className="congreso-org-tile__desc">
                      Pago Redsys, perfiles por plan y flujo actual en esta web.
                    </span>
                  </button>
                  <button
                    type="button"
                    role="radio"
                    aria-checked={String(edicion.organizador || "actedi").toLowerCase() === "otro"}
                    className={
                      "congreso-org-tile" +
                      (String(edicion.organizador || "actedi").toLowerCase() === "otro"
                        ? " congreso-org-tile--selected"
                        : "")
                    }
                    onClick={() => setEdicion({ ...edicion, organizador: "otro" })}
                  >
                    <span className="congreso-org-tile__icon-wrap" aria-hidden>
                      <BusinessOutlinedIcon className="congreso-org-tile__icon" />
                    </span>
                    <span className="congreso-org-tile__label">Otro organizador</span>
                    <span className="congreso-org-tile__desc">
                      Puedes usar inscripción externa (URL) o inscripción con Redsys y planes
                      (configúralo en Inscripciones).
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={ImageOutlinedIcon} title="Portada">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="d-flex align-text-center">
              <div className="col-md-12">
                <Typography variant="subtitle1" component="h3" className="mt-1 mb-1" style={{ fontWeight: 700 }}>
                  Imagen de cabecera
                </Typography>
                <Typography variant="caption" color="textSecondary" component="p" className="mb-2">
                  Tamaño recomendado: <strong>{IMG_TAM_CONGRESO.cabecera}</strong>
                </Typography>
                <CardMaterial>
                  {(selectedImage || edicion.headerImage) && (
                    <CardMedia
                      component="img"
                      alt=""
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        height: "auto",
                        maxHeight: "220px",
                        margin: "12px 0",
                      }}
                      image={
                        selectedImage
                          ? URL.createObjectURL(selectedImage)
                          : `${SERVER_URL}/${edicion.headerImage}`
                      }
                    />
                  )}
                </CardMaterial>
                <div className="congreso-edicion-btn-row">
                  <label htmlFor="upload-congreso-header" className="mb-0">
                    <input
                      ref={imageInputRef}
                      style={{ display: "none" }}
                      id="upload-congreso-header"
                      name="upload-congreso-header"
                      type="file"
                      accept="image/*"
                      onChange={(e) => setSelectedImage(e.target.files[0])}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      component="span"
                      startIcon={<PhotoCameraOutlinedIcon />}
                      className="congreso-edicion-btn-orange"
                    >
                      {selectedImage || (edicion.headerImage && edicion.headerImage !== "")
                        ? "Cambiar imagen de cabecera"
                        : "Imagen de cabecera"}
                    </Button>
                  </label>
                  <Tooltip title="Vista previa">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setOpenPreviewDialog(1)}
                        startIcon={<Visibility />}
                        className="congreso-edicion-btn-orange"
                        disabled={!(selectedImage || (edicion.headerImage && edicion.headerImage !== ""))}
                      >
                        Vista previa
                      </Button>
                    </span>
                  </Tooltip>
                  <PreviewDialog
                    title="Vista previa"
                    open={openPreviewDialog === 1}
                    setOpen={setOpenPreviewDialog}
                    file={selectedImage}
                    src={
                      selectedImage
                        ? URL.createObjectURL(selectedImage)
                        : `${SERVER_URL}/${edicion.headerImage}`
                    }
                  />
                  <Tooltip title="Quitar imagen">
                    <span>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          setSelectedImage(null);
                          setEdicion({ ...edicion, headerImage: "" });
                          if (imageInputRef.current) {
                            imageInputRef.current.value = "";
                          }
                        }}
                        startIcon={<DeleteOutlineIcon />}
                        className="congreso-edicion-btn-orange"
                        disabled={!(selectedImage || (edicion.headerImage && edicion.headerImage !== ""))}
                      >
                        Quitar imagen
                      </Button>
                    </span>
                  </Tooltip>
                </div>
              </div>
            </div>
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={BrandingWatermarkOutlinedIcon} title="Identidad y contacto">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <Typography variant="subtitle1" component="h3" className="mt-1 mb-1" style={{ fontWeight: 700 }}>
              Logo de la edición
            </Typography>
            <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
              Cabecera (franja superior) y pie de la web del congreso.
            </Typography>
            <Typography variant="caption" color="textSecondary" component="p" className="mb-2">
              Tamaño recomendado: <strong>{IMG_TAM_CONGRESO.logoEdicion}</strong>
            </Typography>
            <CardMaterial className="mb-4">
              {edicion.editionLogo ? (
                <CardMedia
                  component="img"
                  alt=""
                  style={{
                    objectFit: "contain",
                    width: "auto",
                    maxWidth: "280px",
                    maxHeight: "120px",
                    margin: "12px",
                    background: "#f5f5f5",
                    padding: "8px",
                    borderRadius: "8px",
                  }}
                  image={
                    String(edicion.editionLogo).startsWith("http")
                      ? edicion.editionLogo
                      : `${SERVER_URL}/${edicion.editionLogo}`
                  }
                />
              ) : null}
            </CardMaterial>
            <div className="congreso-edicion-btn-row mb-4">
              <label htmlFor="upload-edition-logo" className="mb-0">
                <input
                  style={{ display: "none" }}
                  id="upload-edition-logo"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) uploadEditionLogo(f);
                    e.target.value = "";
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  component="span"
                  startIcon={<PhotoCameraOutlinedIcon />}
                  className="congreso-edicion-btn-orange"
                >
                  {edicion.editionLogo ? "Cambiar logo" : "Subir logo"}
                </Button>
              </label>
              {edicion.editionLogo ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteOutlineIcon />}
                  onClick={clearEditionLogo}
                  className="congreso-edicion-btn-orange"
                >
                  Quitar logo
                </Button>
              ) : null}
            </div>

            <TextField
              id="contacto-email"
              label="Correo de contacto del congreso"
              type="email"
              value={edicion.contactoEmail || ""}
              onChange={(e) => setEdicion({ ...edicion, contactoEmail: e.target.value })}
              InputLabelProps={{ shrink: true }}
              margin="normal"
              variant="outlined"
              fullWidth
              helperText="Aparece arriba a la derecha (franja superior azul) y en el pie de la web."
            />

            <Typography variant="subtitle1" component="h3" className="mt-4 mb-2" style={{ fontWeight: 700 }}>
              Organizadores
            </Typography>
            <div className="mb-3">
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => addPatrocListItem("organizadores")}
                className="congreso-edicion-btn-orange"
              >
                Añadir organizador
              </Button>
            </div>
            {renderPatrocListCards("organizadores", {
              rowLabel: (i) => `Organizador ${i + 1}`,
              logoMaxPx: 88,
              webLabel: "Web del organizador (opcional)",
              removeAria: "Quitar organizador",
              tamanoLogoHint: IMG_TAM_CONGRESO.logoOrganizador,
            })}
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={SubjectOutlinedIcon} title="Textos Portada">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="congreso-edicion-edit">
              <Typography variant="subtitle2" color="textSecondary" className="mb-2" component="p">
                Por idioma: distintivo superior (badge), subtítulo del congreso, sede, fechas y programa
                descargable en PDF.
              </Typography>
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={renderGeneralLangFields}
              />
            </div>
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={PaletteOutlinedIcon} title="Tema">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="congreso-edicion-colores row">
              <div className="col-md-4 mb-3 mb-md-0">
                <div className="congreso-edicion-color-field">
                  <span className="congreso-edicion-color-field__label">Color de fondo</span>
                  <div className="congreso-edicion-color-field__row">
                    <label
                      className="congreso-edicion-color-swatch"
                      htmlFor="congreso-nav-bg-color"
                    >
                      <span
                        className="congreso-edicion-color-swatch__preview"
                        style={{ backgroundColor: navBgPickerValue }}
                        aria-hidden
                      />
                      <input
                        id="congreso-nav-bg-color"
                        type="color"
                        className="congreso-edicion-color-swatch__input"
                        value={navBgPickerValue}
                        onChange={(e) =>
                          setEdicion({ ...edicion, themeNavBackground: e.target.value })
                        }
                      />
                    </label>
                    <IconButton
                      size="small"
                      onClick={() => setEdicion({ ...edicion, themeNavBackground: "" })}
                      aria-label="Restablecer color de fondo al valor por defecto"
                    >
                      <RestoreOutlinedIcon fontSize="small" />
                    </IconButton>
                    <code className="congreso-edicion-color-field__hex">
                      {edicion.themeNavBackground && isHex6(edicion.themeNavBackground)
                        ? edicion.themeNavBackground
                        : "—"}
                    </code>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3 mb-md-0">
                <div className="congreso-edicion-color-field">
                  <span className="congreso-edicion-color-field__label">Color destacado</span>
                  <div className="congreso-edicion-color-field__row">
                    <label
                      className="congreso-edicion-color-swatch"
                      htmlFor="congreso-nav-accent-color"
                    >
                      <span
                        className="congreso-edicion-color-swatch__preview"
                        style={{ backgroundColor: navAccentPickerValue }}
                        aria-hidden
                      />
                      <input
                        id="congreso-nav-accent-color"
                        type="color"
                        className="congreso-edicion-color-swatch__input"
                        value={navAccentPickerValue}
                        onChange={(e) =>
                          setEdicion({ ...edicion, themeNavAccent: e.target.value })
                        }
                      />
                    </label>
                    <IconButton
                      size="small"
                      onClick={() => setEdicion({ ...edicion, themeNavAccent: "" })}
                      aria-label="Restablecer color destacado al valor por defecto"
                    >
                      <RestoreOutlinedIcon fontSize="small" />
                    </IconButton>
                    <code className="congreso-edicion-color-field__hex">
                      {edicion.themeNavAccent && isHex6(edicion.themeNavAccent)
                        ? edicion.themeNavAccent
                        : "—"}
                    </code>
                  </div>
                </div>
              </div>
              <div className="col-md-4 mb-3 mb-md-0">
                <div className="congreso-edicion-color-field">
                  <span className="congreso-edicion-color-field__label">
                    Color acento (programa y comunicación)
                  </span>
                  <div className="congreso-edicion-color-field__row">
                    <label
                      className="congreso-edicion-color-swatch"
                      htmlFor="congreso-accent-secondary-color"
                    >
                      <span
                        className="congreso-edicion-color-swatch__preview"
                        style={{ backgroundColor: accentSecondaryPickerValue }}
                        aria-hidden
                      />
                      <input
                        id="congreso-accent-secondary-color"
                        type="color"
                        className="congreso-edicion-color-swatch__input"
                        value={accentSecondaryPickerValue}
                        onChange={(e) =>
                          setEdicion({ ...edicion, themeAccentSecondary: e.target.value })
                        }
                      />
                    </label>
                    <IconButton
                      size="small"
                      onClick={() => setEdicion({ ...edicion, themeAccentSecondary: "" })}
                      aria-label="Restablecer color de acento secundario al valor por defecto"
                    >
                      <RestoreOutlinedIcon fontSize="small" />
                    </IconButton>
                    <code className="congreso-edicion-color-field__hex">
                      {edicion.themeAccentSecondary && isHex6(edicion.themeAccentSecondary)
                        ? edicion.themeAccentSecondary
                        : "—"}
                    </code>
                  </div>
                </div>
              </div>
            </div>
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={WebOutlinedIcon} title="Footer (pie de página web)">
          <fieldset
            disabled={delegadoFormLocked}
            className="congreso-edicion-edit"
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <MultilanguageTabBlock
              contentDisabled={delegadoFormLocked}
              multilanguageTabContent={renderFooterForLang}
            />
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={TuneOutlinedIcon} title="Estado de la edición">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="mt-2 mb-1 d-flex flex-wrap" style={{ gap: "1rem" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={edicion.active !== false}
                    onChange={() => setEdicion({ ...edicion, active: !edicion.active })}
                    name="checkActive"
                  />
                }
                label="Activa"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!edicion.published}
                    onChange={() => setEdicion({ ...edicion, published: !edicion.published })}
                    name="checkPublished"
                  />
                }
                label="Publicada"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={!!edicion.isDefault}
                    onChange={() => setEdicion({ ...edicion, isDefault: !edicion.isDefault })}
                    name="checkDefault"
                  />
                }
                label="Por defecto"
              />
            </div>
          </fieldset>
        </GeneralApartado>

        <GeneralApartado Icon={MailOutlineIcon} title="Mails Inscripciones">
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <Typography variant="body2" color="textSecondary" component="p" className="mb-3">
              Correos que recibirán los avisos de esta edición (inscripciones pagadas, patrocinios,
              envíos de trabajo y contacto), además del correo administrador habitual de ACTEDI.
            </Typography>
            <div className="d-flex align-items-start flex-wrap" style={{ gap: "0.75rem" }}>
              <TextField
                label="Añadir email(s)"
                type="text"
                value={mailAvisoDraft}
                onChange={(e) => setMailAvisoDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addMailInscripcionAviso();
                  }
                }}
                InputLabelProps={{ shrink: true }}
                variant="outlined"
                size="small"
                style={{ minWidth: 260, flex: "1 1 220px" }}
                helperText="Puedes pegar varios separados por ;"
                placeholder="a@ejemplo.com; b@ejemplo.com"
              />
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addMailInscripcionAviso}
                style={{ backgroundColor: "#f3691a", color: "#fff" }}
              >
                Añadir
              </Button>
            </div>
            <div className="mt-3 d-flex flex-wrap" style={{ gap: "0.5rem" }}>
              {(edicion.mailsInscripciones || []).length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                  No hay emails adicionales configurados.
                </Typography>
              ) : (
                (edicion.mailsInscripciones || []).map((mail) => (
                  <Chip
                    key={mail}
                    label={mail}
                    onDelete={() => removeMailInscripcionAviso(mail)}
                    color="default"
                    variant="outlined"
                  />
                ))
              )}
            </div>
          </fieldset>
        </GeneralApartado>
      </>
    );
  };

  const setPatrocListNombre = (listKey, index, val) => {
    const list = [...(edicion[listKey] || [])];
    const row = list[index] || { nombre: "", logo: "", url: "" };
    list[index] = {
      ...row,
      nombre: val,
    };
    setEdicion({ ...edicion, [listKey]: list });
  };

  const addPatrocListItem = (listKey) => {
    setEdicion({
      ...edicion,
      [listKey]: [...(edicion[listKey] || []), { nombre: "", logo: "", url: "" }],
    });
  };

  const removePatrocListItem = (listKey, index) => {
    const list = [...(edicion[listKey] || [])];
    list.splice(index, 1);
    setEdicion({ ...edicion, [listKey]: list });
  };

  const updatePatrocListUrl = (listKey, index, val) => {
    const list = [...(edicion[listKey] || [])];
    const row = { ...(list[index] || { nombre: "", logo: "", url: "" }) };
    list[index] = { ...row, url: val };
    setEdicion({ ...edicion, [listKey]: list });
  };

  const clearPatrocListLogo = (listKey, index) => {
    const list = [...(edicion[listKey] || [])];
    const row = { ...(list[index] || { nombre: "", logo: "", url: "" }) };
    list[index] = { ...row, logo: "" };
    setEdicion({ ...edicion, [listKey]: list });
  };

  const uploadPatrocListLogo = async (listKey, index, file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const fileUrl = res?.data?.fileURL;
      if (!fileUrl) {
        alertError({ customMessage: "No se recibió URL del fichero." });
        return;
      }
      const list = [...(edicion[listKey] || [])];
      const row = { ...(list[index] || { nombre: "", logo: "", url: "" }) };
      list[index] = { ...row, logo: fileUrl };
      setEdicion({ ...edicion, [listKey]: list });
    } catch (error) {
      const logoErr =
        listKey === "sponsors"
          ? "No se pudo subir el logo del sponsor."
          : listKey === "organizadores"
            ? "No se pudo subir el logo del organizador."
            : "No se pudo subir el logo del patrocinador.";
      alertError({
        error,
        customMessage: logoErr,
      });
    }
  };

  const uploadPatrocinioOpcionesPdf = async (file) => {
    if (!file) return;
    const okType = file.type === "application/pdf" || /\.pdf$/i.test(file.name || "");
    if (!okType) {
      alertError({ customMessage: "Solo se admite un archivo PDF." });
      return;
    }
    try {
      const res = await uploadSingleFile(file, "congresos");
      const url = res?.data?.fileURL;
      if (!url) {
        alertError({ customMessage: "No se recibió la URL del archivo subido." });
        return;
      }
      setEdicion({ ...edicion, patrocinioOpcionesPdf: url });
      alertSuccess({ customMessage: "PDF «Opciones de patrocinio» guardado en el borrador. Pulsa Guardar para persistir." });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo subir el PDF de opciones de patrocinio.",
      });
    }
  };

  const clearPatrocinioOpcionesPdf = () => {
    setEdicion({ ...edicion, patrocinioOpcionesPdf: "" });
  };

  const uploadEditionLogo = async (file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const fileUrl = res?.data?.fileURL;
      if (!fileUrl) {
        alertError({ customMessage: "No se recibió URL del fichero." });
        return;
      }
      setEdicion({ ...edicion, editionLogo: fileUrl });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo subir el logo de la edición.",
      });
    }
  };

  const clearEditionLogo = () => {
    setEdicion({ ...edicion, editionLogo: "" });
  };

  const uploadCronogramaImage = async (file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const fileUrl = res?.data?.fileURL;
      if (!fileUrl) {
        alertError({ customMessage: "No se recibió URL del fichero." });
        return;
      }
      setEdicion({ ...edicion, cronogramaImage: fileUrl });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo subir la imagen del cronograma.",
      });
    }
  };

  const clearCronogramaImage = () => {
    setEdicion({ ...edicion, cronogramaImage: "" });
  };

  const uploadSedeImagen = async (file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const fileUrl = res?.data?.fileURL;
      if (!fileUrl) {
        alertError({ customMessage: "No se recibió URL del fichero." });
        return;
      }
      patchSedeStr("imagen", fileUrl);
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo subir la imagen de la sede.",
      });
    }
  };

  const clearSedeImagen = () => {
    patchSedeStr("imagen", "");
  };

  const uploadPosterPublicitario = async (file) => {
    if (!file) return;
    try {
      const res = await uploadSingleFile(file, "congresos");
      const fileUrl = res?.data?.fileURL;
      if (!fileUrl) {
        alertError({ customMessage: "No se recibió URL del fichero." });
        return;
      }
      setEdicion({ ...edicion, posterPublicitario: fileUrl });
    } catch (error) {
      alertError({
        error,
        customMessage: "No se pudo subir el póster publicitario.",
      });
    }
  };

  const clearPosterPublicitario = () => {
    setEdicion({ ...edicion, posterPublicitario: "" });
  };

  const renderPatrocListNombreField = (listKey, index) => {
    const list = edicion[listKey] || [];
    const row = list[index];
    if (!row) return null;
    const label =
      listKey === "sponsors"
        ? "Nombre del sponsor"
        : listKey === "organizadores"
          ? "Nombre del organizador"
          : "Nombre del patrocinador";
    return (
      <TextField
        className="flex-grow-1"
        fullWidth
        multiline
        minRows={2}
        maxRows={6}
        variant="outlined"
        margin="dense"
        label={label}
        value={typeof row.nombre === "string" ? row.nombre : coerceI18nObjectToString(row.nombre)}
        onChange={(e) => setPatrocListNombre(listKey, index, e.target.value)}
        InputLabelProps={{ shrink: true }}
      />
    );
  };

  const renderPatrocListCards = (listKey, { rowLabel, logoMaxPx, webLabel, removeAria, tamanoLogoHint = "" }) =>
    (edicion[listKey] || []).map((row, index) => (
      <CardMaterial key={`${listKey}-${index}`} className="mb-4" style={{ border: "1px solid #ebedf3" }}>
        <CardBody>
          <div className="d-flex justify-content-between align-items-start mb-2">
            <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
              {rowLabel(index)}
            </Typography>
            <IconButton
              size="small"
              onClick={() => removePatrocListItem(listKey, index)}
              aria-label={removeAria}
              disabled={delegadoFormLocked}
            >
              <Delete fontSize="small" />
            </IconButton>
          </div>
          <fieldset
            disabled={delegadoFormLocked}
            style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
          >
            <div className="row">
              <div className="col-md-6 mb-3">
                <div className="mb-2">
                  <Typography variant="caption" color="textSecondary" display="block">
                    Logo (opcional, imagen)
                  </Typography>
                  {tamanoLogoHint ? (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Tamaño recomendado: <strong>{tamanoLogoHint}</strong>
                    </Typography>
                  ) : null}
                </div>
                {row.logo ? (
                  <div className="mb-2">
                    <CardMedia
                      component="img"
                      alt=""
                      style={{
                        objectFit: "contain",
                        maxHeight: `${logoMaxPx}px`,
                        maxWidth: listKey === "sponsors" ? "320px" : "220px",
                        background: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                      image={
                        String(row.logo).startsWith("http")
                          ? row.logo
                          : `${SERVER_URL}/${row.logo}`
                      }
                    />
                  </div>
                ) : null}
                <label htmlFor={`upload-${listKey}-logo-${index}`}>
                  <input
                    style={{ display: "none" }}
                    id={`upload-${listKey}-logo-${index}`}
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const f = e.target.files && e.target.files[0];
                      if (f) uploadPatrocListLogo(listKey, index, f);
                      e.target.value = "";
                    }}
                  />
                  <Button color="secondary" component="span" variant="outlined" size="small">
                    {row.logo ? "Cambiar logo" : "Subir logo"}
                  </Button>
                </label>
                {row.logo ? (
                  <Button size="small" className="ml-2" onClick={() => clearPatrocListLogo(listKey, index)}>
                    Quitar logo
                  </Button>
                ) : null}
              </div>
              <div className="col-md-6 mb-3">
                <TextField
                  fullWidth
                  variant="outlined"
                  margin="dense"
                  label={webLabel}
                  placeholder="https://…"
                  value={row.url || ""}
                  onChange={(e) => updatePatrocListUrl(listKey, index, e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </div>
            </div>
            <div className="col-12 mb-2">{renderPatrocListNombreField(listKey, index)}</div>
          </fieldset>
        </CardBody>
      </CardMaterial>
    ));

  if (isLoadingData) return <ContentSkeleton />;

  return (
    <>
      <Card>
        {renderStickyButtons({
          onBack: () => history.push("/congresos"),
          onSave: saveEdicion,
          readOnlySave: delegadoFormLocked,
          hasUnsavedChanges: hasUnsavedEdicion,
          onDelete: () => setOpenConfirmDialog(true),
          showDelete:
            !!edicionId &&
            ((canEditGestionModule(user?.role) &&
              !isReadOnlyConsultor(user?.role)) ||
              (isCongresoDelegado(user?.role) && myAccess?.canDelete)),
          showShare:
            !!edicionId && canManageCongresoDelegadosUi(user?.role),
          onOpenShare: () => setOpenShareDialog(true),
        })}
        <CardHeader
          title={
            edicionId
              ? "Editar edición de congreso"
              : "Nueva edición de congreso"
          }
        />
        <CardBody>
          <div className="congreso-edicion-apartados" role="tablist" aria-label="Apartados de la edición">
            {APARTADOS_CONGRESO.map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                role="tab"
                aria-selected={mainTab === id}
                className={
                  "congreso-edicion-apartado" +
                  (mainTab === id ? " congreso-edicion-apartado--active" : "") +
                  (delegadoTabDisabled(id) ? " congreso-edicion-apartado--disabled" : "")
                }
                disabled={delegadoTabDisabled(id)}
                onClick={() => {
                  if (delegadoTabDisabled(id)) return;
                  setMainTab(id);
                }}
              >
                <Icon className="congreso-edicion-apartado__icon" />
                <span className="congreso-edicion-apartado__label">{label}</span>
              </button>
            ))}
          </div>

          {mainTab === MAIN_TAB_GENERAL && (
            <div className="congreso-edicion-tab-panel">{renderGeneralFields()}</div>
          )}

          {mainTab === MAIN_TAB_CARTA && (
            <div className="congreso-edicion-edit congreso-edicion-tab-panel">
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={(lang) => (
                  <Editor
                    body={
                      (edicion.cartaBienvenida && edicion.cartaBienvenida[lang]) || ""
                    }
                    setBody={(v) => handleChangeEditor("cartaBienvenida", lang, v)}
                    className="max-height"
                    lang={lang}
                  />
                )}
              />
            </div>
          )}

          {mainTab === MAIN_TAB_CRONOGRAMA && (
            <div className="congreso-edicion-tab-panel">
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
                  Imagen del cronograma que se mostrará en la web del congreso, justo después de la carta de
                  bienvenida.
                </Typography>
                <Typography variant="caption" color="textSecondary" component="p" className="mb-3">
                  Tamaño recomendado: <strong>{IMG_TAM_CONGRESO.cronograma}</strong>
                </Typography>
                <CardMaterial className="mb-4">
                  {edicion.cronogramaImage ? (
                    <CardMedia
                      component="img"
                      alt=""
                      style={{
                        objectFit: "contain",
                        width: "100%",
                        maxHeight: "480px",
                        margin: "12px auto",
                        background: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                      image={
                        String(edicion.cronogramaImage).startsWith("http")
                          ? edicion.cronogramaImage
                          : `${SERVER_URL}/${edicion.cronogramaImage}`
                      }
                    />
                  ) : null}
                </CardMaterial>
                <div className="congreso-edicion-btn-row">
                  <label htmlFor="upload-cronograma-image" className="mb-0">
                    <input
                      style={{ display: "none" }}
                      id="upload-cronograma-image"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) uploadCronogramaImage(f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      component="span"
                      startIcon={<PhotoCameraOutlinedIcon />}
                      className="congreso-edicion-btn-orange"
                    >
                      {edicion.cronogramaImage ? "Cambiar imagen" : "Subir imagen"}
                    </Button>
                  </label>
                  {edicion.cronogramaImage ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={clearCronogramaImage}
                      className="congreso-edicion-btn-orange"
                    >
                      Quitar imagen
                    </Button>
                  ) : null}
                </div>
              </fieldset>
            </div>
          )}

          {mainTab === MAIN_TAB_COMITES && (
            <div className="congreso-edicion-tab-panel">
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={renderComitesForLang}
              />
            </div>
          )}

          {mainTab === MAIN_TAB_TALLERES && (
            <div className="congreso-edicion-tab-panel">
              <CongresoTalleresEditor
                talleresPorDias={edicion.talleresPorDias || []}
                readOnly={delegadoFormLocked}
                onChange={(next) => setEdicion({ ...edicion, talleresPorDias: next })}
              />
            </div>
          )}

          {mainTab === MAIN_TAB_PROGRAMA && (
            <div className="congreso-edicion-tab-panel">
              <CongresoProgramaCalendario
                programaPorDias={edicion.programaPorDias || []}
                readOnly={delegadoFormLocked}
                onChange={(next) =>
                  setEdicion({ ...edicion, programaPorDias: next })
                }
              />
            </div>
          )}

          {mainTab === MAIN_TAB_COMUNICACION && (
            <div className="congreso-edicion-edit congreso-edicion-tab-panel">
              <GeneralApartado Icon={ChatBubbleOutlineIcon} title="Introducción «Comunicaciones» (tarjetas)">
                <Typography variant="body2" color="textSecondary" className="mb-2">
                  Textos de las tarjetas de la página de comunicaciones y PDF de normativa por idioma. Los acordeones
                  largos de guías y normativa están en la pestaña «Guías y normas».
                </Typography>
                <MultilanguageTabBlock
                  contentDisabled={delegadoFormLocked}
                  multilanguageTabContent={renderComunicacionIntroLang}
                />
              </GeneralApartado>
            </div>
          )}

          {mainTab === MAIN_TAB_GUIAS_NORMAS && (
            <div className="congreso-edicion-edit congreso-edicion-tab-panel">
              <GeneralApartado Icon={SubjectOutlinedIcon} title="Guías y normas (acordeones en la web)">
                <fieldset
                  disabled={delegadoFormLocked}
                  style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
                >
                  {renderGuiasNormasItemsEditors()}
                </fieldset>
              </GeneralApartado>
            </div>
          )}

          {mainTab === MAIN_TAB_SEDE && (
            <div className="congreso-edicion-edit congreso-edicion-tab-panel">
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                {renderSedeTitleDescMultilang()}
              </fieldset>
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                {renderSedeUnilingual()}
              </fieldset>
              <Typography variant="subtitle2" className="mt-2 mb-1">
                Imagen de la sede (lateral en la web)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
                Tamaño recomendado: <strong>{IMG_TAM_CONGRESO.sedeLateral}</strong>
              </Typography>
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <CardMaterial className="mb-4">
                  {sedeMerged().imagen ? (
                    <CardMedia
                      component="img"
                      alt=""
                      style={{
                        objectFit: "cover",
                        width: "100%",
                        maxHeight: "360px",
                        margin: "12px auto",
                        background: "#f5f5f5",
                        padding: "8px",
                        borderRadius: "8px",
                      }}
                      image={
                        String(sedeMerged().imagen).startsWith("http")
                          ? sedeMerged().imagen
                          : `${SERVER_URL}/${sedeMerged().imagen}`
                      }
                    />
                  ) : null}
                </CardMaterial>
                <div className="congreso-edicion-btn-row mb-4">
                  <label htmlFor="upload-sede-imagen" className="mb-0">
                    <input
                      style={{ display: "none" }}
                      id="upload-sede-imagen"
                      type="file"
                      accept="image/*"
                      onChange={(e) => {
                        const f = e.target.files && e.target.files[0];
                        if (f) uploadSedeImagen(f);
                        e.target.value = "";
                      }}
                    />
                    <Button
                      variant="outlined"
                      size="small"
                      component="span"
                      startIcon={<PhotoCameraOutlinedIcon />}
                      className="congreso-edicion-btn-orange"
                    >
                      {sedeMerged().imagen ? "Cambiar imagen" : "Subir imagen"}
                    </Button>
                  </label>
                  {sedeMerged().imagen ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={clearSedeImagen}
                      className="congreso-edicion-btn-orange"
                    >
                      Quitar imagen
                    </Button>
                  ) : null}
                </div>
              </fieldset>
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
              <TextField
                label="Sitio web"
                fullWidth
                variant="outlined"
                margin="normal"
                value={sedeMerged().webUrl}
                onChange={(e) => patchSedeStr("webUrl", e.target.value)}
                placeholder="https://www.ccib.es"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Sitio web (segunda sede, opcional)"
                fullWidth
                variant="outlined"
                margin="normal"
                value={sedeMerged().webUrl2}
                onChange={(e) => patchSedeStr("webUrl2", e.target.value)}
                placeholder="https://…"
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Enlace del botón «Conoce la sede» (URL o #ancla)"
                fullWidth
                variant="outlined"
                margin="normal"
                value={sedeMerged().comoLlegarUrl}
                onChange={(e) => patchSedeStr("comoLlegarUrl", e.target.value)}
                placeholder="https://… o #como-llegar"
                InputLabelProps={{ shrink: true }}
              />
              <Typography variant="subtitle2" className="mt-2 mb-1">
                Texto del botón (por idioma)
              </Typography>
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={(lang) => (
                  <TextField
                    label="Texto del botón"
                    fullWidth
                    variant="outlined"
                    margin="dense"
                    value={(sedeMerged().textoBoton && sedeMerged().textoBoton[lang]) || ""}
                    onChange={(e) => patchSedeTextoBotonLang(lang, e.target.value)}
                    placeholder="CONOCE LA SEDE"
                    InputLabelProps={{ shrink: true }}
                  />
                )}
              />
              {INFORMACION_GENERAL_MOSTRAR_ORGANIZACION ? (
                <>
              <Typography variant="subtitle2" className="mt-3 mb-1">
                Texto «Organización» (tarjeta derecha en la web)
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
                Se muestra junto a los logos de organizadores configurados en «General». Puedes usar negritas y
                párrafos.
              </Typography>
              <MultilanguageTabBlock
                contentDisabled={delegadoFormLocked}
                multilanguageTabContent={(lang) => (
                  <Editor
                    body={(edicion.organizacionIntroHtml && edicion.organizacionIntroHtml[lang]) || ""}
                    setBody={(v) => handleChangeEditor("organizacionIntroHtml", lang, v)}
                    className="max-height"
                    lang={lang}
                  />
                )}
              />
                </>
              ) : null}
              </fieldset>
              <div className="congreso-edicion-programa-pdf-card mb-4">
                <div className="congreso-edicion-programa-pdf-card__head">
                  <PictureAsPdfIcon className="congreso-edicion-programa-pdf-card__icon" aria-hidden />
                  <div>
                    <Typography variant="subtitle1" component="h3" className="mb-1" style={{ fontWeight: 700 }}>
                      Póster publicitario del congreso
                    </Typography>
                    <Typography variant="body2" color="textSecondary" component="p">
                      Archivo descargable en la sección «Información general» de la web (PDF o imagen).
                    </Typography>
                  </div>
                </div>
                <fieldset
                  disabled={delegadoFormLocked}
                  style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
                >
                  <div className="congreso-edicion-btn-row">
                    <label className="mb-0">
                      <input
                        type="file"
                        accept="image/*,application/pdf,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) uploadPosterPublicitario(f);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={<CloudUploadOutlinedIcon />}
                        className="congreso-edicion-btn-orange"
                      >
                        Subir póster
                      </Button>
                    </label>
                    {edicion.posterPublicitario ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={clearPosterPublicitario}
                          className="congreso-edicion-btn-orange"
                        >
                          Eliminar póster
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          component="a"
                          href={
                            String(edicion.posterPublicitario).startsWith("http")
                              ? edicion.posterPublicitario
                              : `${SERVER_URL}/${String(edicion.posterPublicitario).replace(/^\//, "")}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={
                            /\.pdf$/i.test(String(edicion.posterPublicitario)) ? (
                              <PictureAsPdfIcon />
                            ) : (
                              <ImageOutlinedIcon />
                            )
                          }
                          className="congreso-edicion-btn-orange"
                        >
                          {/\.pdf$/i.test(String(edicion.posterPublicitario))
                            ? "Ver PDF"
                            : "Ver póster"}
                        </Button>
                      </>
                    ) : null}
                  </div>
                </fieldset>
              </div>
              <Typography variant="subtitle2" className="mt-3 mb-1">
                Ubicación Google Maps
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" className="mb-2">
                Nombre o dirección del lugar que debe mostrar el mapa en la página «Contacto» (por ejemplo el mismo
                que la sede del congreso). Si el punto del mapa no coincide con la dirección de sede, escribe aquí el
                texto que quieras que busque Google Maps. Si lo dejas vacío, se usará la dirección de sede. También
                puedes pegar la URL completa del iframe de Google Maps (https://www.google.com/maps/embed…).
              </Typography>
              <TextField
                label="Ubicación o URL del mapa (opcional)"
                fullWidth
                variant="outlined"
                margin="normal"
                value={edicion.contactoMapsIframeSrc || ""}
                onChange={(e) =>
                  setEdicion({ ...edicion, contactoMapsIframeSrc: e.target.value })
                }
                placeholder="Ej. Palacio de Congresos de Valencia"
                InputLabelProps={{ shrink: true }}
                disabled={delegadoFormLocked}
              />
            </div>
          )}

          {mainTab === MAIN_TAB_INSCRIPCION && (
            <div className="congreso-edicion-edit congreso-edicion-tab-panel">
              <div className="congreso-com-intro-editor mb-4">
                <p className="congreso-com-intro-editor__hint">
                  Texto que aparece bajo el título «Inscripciones» en la web del congreso, con el mismo estilo que el
                  subtítulo de «Comunicaciones y Pósters» (p. ej. participa, consulta plazos…). Puedes usar varias
                  líneas.
                </p>
                <MultilanguageTabBlock
                  contentDisabled={delegadoFormLocked}
                  multilanguageTabContent={(lang) => (
                    <div className="congreso-com-intro-editor__lead">
                      <TextField
                        label="Texto introductorio"
                        fullWidth
                        variant="outlined"
                        margin="dense"
                        multiline
                        minRows={3}
                        value={(edicion.inscripcionSubtitulo && edicion.inscripcionSubtitulo[lang]) || ""}
                        onChange={handleChange("inscripcionSubtitulo", lang)}
                        InputLabelProps={{ shrink: true }}
                      />
                    </div>
                  )}
                />
              </div>
              {isOrganizadorOtroEdicion(edicion) ? (
                <CardMaterial className="mb-4" style={{ border: "1px solid #ebedf3" }}>
                  <CardHeader title="Modo de inscripción (otro organizador)" />
                  <CardBody>
                    <fieldset
                      disabled={delegadoFormLocked}
                      style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
                    >
                      <Typography variant="body2" color="textSecondary" className="mb-3">
                        Elige si la inscripción se hace con un enlace externo o con el formulario
                        integrado y cobro Redsys (planes, perfiles y talleres como en ACTEDI).
                      </Typography>
                      <div
                        className="congreso-org-tiles"
                        role="radiogroup"
                        aria-label="Modo de inscripción para otro organizador"
                      >
                        <button
                          type="button"
                          role="radio"
                          aria-checked={
                            getInscripcionOrganizadorModoEdicion(edicion) !== "redsys"
                          }
                          className={
                            "congreso-org-tile" +
                            (getInscripcionOrganizadorModoEdicion(edicion) !== "redsys"
                              ? " congreso-org-tile--selected"
                              : "")
                          }
                          onClick={() =>
                            setEdicion({
                              ...edicion,
                              inscripcionOrganizadorModo: "externa",
                            })
                          }
                        >
                          <span className="congreso-org-tile__icon-wrap" aria-hidden>
                            <LinkOutlinedIcon className="congreso-org-tile__icon" />
                          </span>
                          <span className="congreso-org-tile__label">Inscripción externa</span>
                          <span className="congreso-org-tile__desc">
                            La web solo mostrará un botón para redirigir al enlace externo.
                          </span>
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={
                            getInscripcionOrganizadorModoEdicion(edicion) === "redsys"
                          }
                          className={
                            "congreso-org-tile" +
                            (getInscripcionOrganizadorModoEdicion(edicion) === "redsys"
                              ? " congreso-org-tile--selected"
                              : "")
                          }
                          onClick={() =>
                            setEdicion({
                              ...edicion,
                              inscripcionOrganizadorModo: "redsys",
                            })
                          }
                        >
                          <span className="congreso-org-tile__mark" aria-hidden>
                            REDSYS
                          </span>
                          <span className="congreso-org-tile__label">Inscripción con Redsys</span>
                          <span className="congreso-org-tile__desc">
                            Formulario integrado, planes y cobro con Redsys de esta edición.
                          </span>
                        </button>
                      </div>
                      {getInscripcionOrganizadorModoEdicion(edicion) !== "redsys" ? (
                        <>
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            className="mt-4 mb-2"
                          >
                            La web muestra un único botón «Inscribirse». Usa una URL completa
                            (https://…).
                          </Typography>
                          <TextField
                            label="URL de inscripción externa"
                            fullWidth
                            variant="outlined"
                            margin="normal"
                            value={edicion.inscripcionUrlExterna || ""}
                            onChange={(e) =>
                              setEdicion({
                                ...edicion,
                                inscripcionUrlExterna: e.target.value,
                              })
                            }
                            InputLabelProps={{ shrink: true }}
                            placeholder="https://..."
                            helperText="Enlace al que redirigirá la web cuando elijan inscribirse."
                          />
                        </>
                      ) : (
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          className="mt-4 mb-0"
                        >
                          Configura los planes abajo. Las credenciales Redsys de esta edición
                          están en la pestaña <strong>Organizador</strong>.
                        </Typography>
                      )}
                    </fieldset>
                  </CardBody>
                </CardMaterial>
              ) : null}
              <div className="congreso-com-intro-editor mb-4">
                <p className="congreso-com-intro-editor__hint">
                  <strong>Contenido bajo la inscripción</strong> (misma sección en la web del congreso): normativa,
                  avisos o <strong>tablas</strong> (p. ej. tarifas). Editor HTML por idioma; si está vacío, no se
                  muestra nada debajo del botón o de las tarjetas de planes.
                </p>
                <MultilanguageTabBlock
                  contentDisabled={delegadoFormLocked}
                  multilanguageTabContent={(lang) => (
                    <Editor
                      body={(edicion.inscripcionesBloqueHtml && edicion.inscripcionesBloqueHtml[lang]) || ""}
                      setBody={(v) => handleChangeEditor("inscripcionesBloqueHtml", lang, v)}
                      className="max-height"
                      lang={lang}
                    />
                  )}
                />
              </div>
              {usaPlanesInscripcionIntegrados(edicion) ? (
                <>
              {(mergeInscripcionPlanes(edicion.inscripcionPlanes || []) || []).map((plan, pi) => (
                <Card key={plan.planId || pi} className="mb-4">
                  <CardHeader title={`Plan ${pi + 1}`} />
                  <CardBody>
                    <fieldset
                      disabled={delegadoFormLocked}
                      style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
                    >
                    <div className="d-flex justify-content-end mb-2">
                      <IconButton
                        size="small"
                        aria-label="Eliminar plan"
                        onClick={() => removeInscripcionPlan(pi)}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </div>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={!!plan.recomendado}
                          onChange={() => togglePlanRecomendado(pi)}
                          color="primary"
                        />
                      }
                      label="Recomendado"
                    />
                    {usaPlanesInscripcionIntegrados(edicion) ? (
                      <>
                        <Typography variant="subtitle2" className="mt-2 mb-1">
                          Perfiles que pueden elegir este plan
                        </Typography>
                        <Typography variant="caption" color="textSecondary" component="p" className="mb-2">
                          Si no marcas ninguno, el plan es válido para cualquier perfil (no socio, estudiante no
                          socio, socio ACTEDI o estudiante).
                        </Typography>
                        <div className="d-flex flex-wrap">
                          {PERFILES_INSCRIPCION.map((pk) => (
                            <FormControlLabel
                              key={pk}
                              className="mr-3 mb-1"
                              control={
                                <Checkbox
                                  color="primary"
                                  checked={(plan.perfilesPermitidos || []).includes(pk)}
                                  onChange={() => togglePlanPerfilPermitido(pi, pk)}
                                />
                              }
                              label={
                                pk === "no_socio"
                                  ? "No socio"
                                  : pk === "estudiante_no_socio"
                                    ? "Estudiante no socio"
                                    : pk === "socio_actedi"
                                      ? "Socio ACTEDI"
                                      : "Estudiante"
                              }
                            />
                          ))}
                        </div>
                      </>
                    ) : null}
                    <TextField
                      label="Precio (€)"
                      helperText="Un solo valor: pago (Redsys), listados y texto en la web se generan a partir de aquí."
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      type="number"
                      inputProps={{ min: 0, step: 0.01 }}
                      value={
                        plan.precioImporte != null && plan.precioImporte !== ""
                          ? plan.precioImporte
                          : ""
                      }
                      onChange={(e) =>
                        updatePlanScalarField(
                          pi,
                          "precioImporte",
                          normalizePrecioImporte(e.target.value)
                        )
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="Precio sin oferta (opcional)"
                      helperText="Solo presentación en la web: se muestra tachado junto al precio con oferta. No afecta al cobro."
                      fullWidth
                      variant="outlined"
                      margin="normal"
                      value={plan.precioTextoAnterior || ""}
                      onChange={(e) =>
                        updatePlanScalarField(pi, "precioTextoAnterior", e.target.value)
                      }
                      InputLabelProps={{ shrink: true }}
                    />
                    <div className="row mt-1">
                      <div className="col-md-6">
                        <TextField
                          label="Inicio venta inscripción (opcional)"
                          type="date"
                          fullWidth
                          variant="outlined"
                          margin="normal"
                          InputLabelProps={{ shrink: true }}
                          value={plan.inscripcionDesde || ""}
                          onChange={(e) =>
                            updatePlanScalarField(pi, "inscripcionDesde", e.target.value)
                          }
                          helperText="Vacío = sin límite. Fecha inclusive (UTC)."
                        />
                      </div>
                      <div className="col-md-6">
                        <TextField
                          label="Fin venta inscripción (opcional)"
                          type="date"
                          fullWidth
                          variant="outlined"
                          margin="normal"
                          InputLabelProps={{ shrink: true }}
                          value={plan.inscripcionHasta || ""}
                          onChange={(e) =>
                            updatePlanScalarField(pi, "inscripcionHasta", e.target.value)
                          }
                          helperText="Vacío = sin límite. Último día inclusive (UTC)."
                        />
                      </div>
                    </div>
                    </fieldset>
                    <MultilanguageTabBlock
                      contentDisabled={delegadoFormLocked}
                      multilanguageTabContent={(lang) =>
                        renderInscripcionPlanLangFields(pi, lang)
                      }
                    />
                  </CardBody>
                </Card>
              ))}
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<AddIcon />}
                onClick={addInscripcionPlan}
                className="mb-4"
              >
                Añadir plan
              </Button>
              </fieldset>
                </>
              ) : null}
            </div>
          )}

          {mainTab === MAIN_TAB_PATROC && (
            <div className="congreso-edicion-tab-panel">
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <div className="congreso-edicion-programa-pdf-card mb-5">
                  <div className="congreso-edicion-programa-pdf-card__head">
                    <PictureAsPdfIcon className="congreso-edicion-programa-pdf-card__icon" aria-hidden />
                    <div>
                      <Typography variant="subtitle1" component="h3" className="mb-1" style={{ fontWeight: 700 }}>
                        Opciones de patrocinio (PDF)
                      </Typography>
                      <Typography variant="body2" color="textSecondary" component="p">
                        PDF único para la web (no va por idioma). Los visitantes lo descargan desde el modal
                        «Información sobre patrocinio», en la sección Patrocinadores del congreso.
                      </Typography>
                    </div>
                  </div>
                  <div className="congreso-edicion-btn-row">
                    <label className="mb-0">
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        style={{ display: "none" }}
                        onChange={(e) => {
                          const f = e.target.files && e.target.files[0];
                          if (f) uploadPatrocinioOpcionesPdf(f);
                          e.target.value = "";
                        }}
                      />
                      <Button
                        variant="outlined"
                        component="span"
                        size="small"
                        startIcon={<CloudUploadOutlinedIcon />}
                        className="congreso-edicion-btn-orange"
                      >
                        {edicion.patrocinioOpcionesPdf ? "Cambiar PDF" : "Subir PDF"}
                      </Button>
                    </label>
                    {edicion.patrocinioOpcionesPdf ? (
                      <>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<DeleteOutlineIcon />}
                          onClick={clearPatrocinioOpcionesPdf}
                          className="congreso-edicion-btn-orange"
                        >
                          Quitar PDF
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          component="a"
                          href={
                            String(edicion.patrocinioOpcionesPdf).startsWith("http")
                              ? edicion.patrocinioOpcionesPdf
                              : `${SERVER_URL}/${String(edicion.patrocinioOpcionesPdf).replace(/^\//, "")}`
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                          startIcon={<PictureAsPdfIcon />}
                          className="congreso-edicion-btn-orange"
                        >
                          Ver PDF
                        </Button>
                      </>
                    ) : null}
                  </div>
                </div>
              </fieldset>

              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <Typography variant="h5" component="h2" className="mb-2" style={{ fontWeight: 800 }}>
                  Sponsors
                </Typography>
                <Typography variant="body2" color="textSecondary" className="mb-3">
                  Misma estructura que patrocinadores: nombre (único para todos los idiomas) y/o logo, web opcional.
                  En la web los <strong>Sponsors</strong> se muestran <strong>antes</strong> y con <strong>logos más grandes</strong>.
                </Typography>
                <div className="mb-3">
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addPatrocListItem("sponsors")}
                    className="congreso-edicion-btn-orange"
                  >
                    Añadir sponsor
                  </Button>
                </div>
              </fieldset>
              {renderPatrocListCards("sponsors", {
                rowLabel: (i) => `Sponsor ${i + 1}`,
                logoMaxPx: 140,
                webLabel: "Web del sponsor (opcional)",
                removeAria: "Quitar sponsor",
                tamanoLogoHint: IMG_TAM_CONGRESO.logoSponsor,
              })}

              <hr className="my-5" style={{ borderColor: "#e4e6ef" }} />

              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <Typography variant="h5" component="h2" className="mb-2" style={{ fontWeight: 800 }}>
                  Patrocinadores
                </Typography>
                <Typography variant="body2" color="textSecondary" className="mb-3">
                  Por cada patrocinador puedes usar solo el <strong>nombre</strong> (único, todos los idiomas) o
                  subir un <strong>logo</strong>: en la web, si hay logo se muestra la imagen; si no, el texto. La
                  web opcional envuelve el logo en un enlace.
                </Typography>
                <div className="mb-3">
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addPatrocListItem("patrocinadores")}
                    className="congreso-edicion-btn-orange"
                  >
                    Añadir patrocinador
                  </Button>
                </div>
              </fieldset>
              {renderPatrocListCards("patrocinadores", {
                rowLabel: (i) => `Patrocinador ${i + 1}`,
                logoMaxPx: 100,
                webLabel: "Web del patrocinador (opcional)",
                removeAria: "Quitar patrocinador",
                tamanoLogoHint: IMG_TAM_CONGRESO.logoPatrocinador,
              })}
            </div>
          )}

          {mainTab === MAIN_TAB_ORGANIZADOR && (
            <div className="congreso-edicion-tab-panel">
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <GeneralApartado Icon={SupervisorAccountOutlinedIcon} title="Organizador">
                  <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
                    Quién gestiona la inscripción en esta web.
                  </Typography>
                  <div className="congreso-org-tiles" role="radiogroup" aria-label="Organizador del congreso">
                    <button
                      type="button"
                      role="radio"
                      aria-checked={
                        String(edicion.organizador || "actedi").toLowerCase() !== "otro"
                      }
                      className={
                        "congreso-org-tile" +
                        (String(edicion.organizador || "actedi").toLowerCase() !== "otro"
                          ? " congreso-org-tile--selected"
                          : "")
                      }
                      onClick={() => setEdicion({ ...edicion, organizador: "actedi" })}
                    >
                      <span className="congreso-org-tile__mark" aria-hidden>
                        ACTEDI
                      </span>
                      <span className="congreso-org-tile__label">Inscripción ACTEDI</span>
                      <span className="congreso-org-tile__desc">
                        Pago Redsys, perfiles por plan y flujo actual en esta web.
                      </span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={String(edicion.organizador || "actedi").toLowerCase() === "otro"}
                      className={
                        "congreso-org-tile" +
                        (String(edicion.organizador || "actedi").toLowerCase() === "otro"
                          ? " congreso-org-tile--selected"
                          : "")
                      }
                      onClick={() => setEdicion({ ...edicion, organizador: "otro" })}
                    >
                      <span className="congreso-org-tile__icon-wrap" aria-hidden>
                        <SupervisorAccountOutlinedIcon className="congreso-org-tile__icon" />
                      </span>
                      <span className="congreso-org-tile__label">Otro organizador</span>
                      <span className="congreso-org-tile__desc">
                        Puedes usar inscripción externa (URL) o inscripción con Redsys y planes
                        (configúralo en Inscripciones).
                      </span>
                    </button>
                  </div>
                  {String(edicion.organizador || "actedi").toLowerCase() === "otro" ? (
                    <>
                      <div className="mt-4 congreso-org-tiles" role="radiogroup" aria-label="Modo de inscripción para otro organizador">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={
                            String(edicion.inscripcionOrganizadorModo || "externa").toLowerCase() !==
                            "redsys"
                          }
                          className={
                            "congreso-org-tile" +
                            (String(edicion.inscripcionOrganizadorModo || "externa").toLowerCase() !==
                            "redsys"
                              ? " congreso-org-tile--selected"
                              : "")
                          }
                          onClick={() =>
                            setEdicion({
                              ...edicion,
                              inscripcionOrganizadorModo: "externa",
                            })
                          }
                        >
                          <span className="congreso-org-tile__icon-wrap" aria-hidden>
                            <LinkOutlinedIcon className="congreso-org-tile__icon" />
                          </span>
                          <span className="congreso-org-tile__label">Inscripción externa</span>
                          <span className="congreso-org-tile__desc">
                            La web solo mostrará un botón para redirigir al enlace externo.
                          </span>
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={
                            String(edicion.inscripcionOrganizadorModo || "externa").toLowerCase() ===
                            "redsys"
                          }
                          className={
                            "congreso-org-tile" +
                            (String(edicion.inscripcionOrganizadorModo || "externa").toLowerCase() ===
                            "redsys"
                              ? " congreso-org-tile--selected"
                              : "")
                          }
                          onClick={() =>
                            setEdicion({
                              ...edicion,
                              inscripcionOrganizadorModo: "redsys",
                            })
                          }
                        >
                          <span className="congreso-org-tile__mark" aria-hidden>
                            REDSYS
                          </span>
                          <span className="congreso-org-tile__label">Inscripción con Redsys</span>
                          <span className="congreso-org-tile__desc">
                            Usa el formulario integrado de la web y cobra con credenciales Redsys de esta edición.
                          </span>
                        </button>
                      </div>

                      {String(edicion.inscripcionOrganizadorModo || "externa").toLowerCase() !== "redsys" ? (
                        <TextField
                          className="mt-4"
                          label="URL de inscripción externa"
                          value={edicion.inscripcionUrlExterna || ""}
                          onChange={(e) =>
                            setEdicion({ ...edicion, inscripcionUrlExterna: e.target.value })
                          }
                          InputLabelProps={{ shrink: true }}
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          helperText="Enlace al que redirigirá la web cuando elijan inscribirse."
                        />
                      ) : (
                        <div className="mt-4">
                          <Typography variant="subtitle1" style={{ fontWeight: 700 }}>
                            Configuración Redsys de la edición
                          </Typography>
                          <Typography variant="body2" color="textSecondary" className="mb-2">
                            Si dejas campos vacíos, se usarán las credenciales globales del servidor.
                          </Typography>
                          <div className="row">
                            <div className="col-md-4">
                              <TextField
                                label="Merchant Code"
                                value={(edicion.redsysConfig && edicion.redsysConfig.merchantCode) || ""}
                                onChange={(e) =>
                                  setEdicion({
                                    ...edicion,
                                    redsysConfig: {
                                      ...(edicion.redsysConfig || {}),
                                      merchantCode: e.target.value,
                                    },
                                  })
                                }
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                variant="outlined"
                                fullWidth
                                helperText="9 dígitos"
                              />
                            </div>
                            <div className="col-md-4">
                              <TextField
                                label="Terminal"
                                value={(edicion.redsysConfig && edicion.redsysConfig.terminal) || ""}
                                onChange={(e) =>
                                  setEdicion({
                                    ...edicion,
                                    redsysConfig: {
                                      ...(edicion.redsysConfig || {}),
                                      terminal: e.target.value,
                                    },
                                  })
                                }
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                variant="outlined"
                                fullWidth
                                helperText="Ej: 001"
                              />
                            </div>
                            <div className="col-md-4">
                              <TextField
                                select
                                label="Entorno Redsys"
                                value={(edicion.redsysConfig && edicion.redsysConfig.environment) || ""}
                                onChange={(e) =>
                                  setEdicion({
                                    ...edicion,
                                    redsysConfig: {
                                      ...(edicion.redsysConfig || {}),
                                      environment: e.target.value,
                                    },
                                  })
                                }
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                variant="outlined"
                                fullWidth
                                helperText="Vacío = heredar entorno global"
                              >
                                <MenuItem value="">Heredar global</MenuItem>
                                <MenuItem value="test">Test</MenuItem>
                                <MenuItem value="production">Producción</MenuItem>
                              </TextField>
                            </div>
                          </div>
                          <TextField
                            label="Secret Key Base64"
                            type="password"
                            value={(edicion.redsysConfig && edicion.redsysConfig.secretKeyBase64) || ""}
                            onChange={(e) =>
                              setEdicion({
                                ...edicion,
                                redsysConfig: {
                                  ...(edicion.redsysConfig || {}),
                                  secretKeyBase64: e.target.value,
                                },
                              })
                            }
                            InputLabelProps={{ shrink: true }}
                            margin="normal"
                            variant="outlined"
                            fullWidth
                            helperText="Opcional. Si lo rellenas, esta edición firmará y verificará pagos con esa clave."
                          />
                        </div>
                      )}

                      <GeneralApartado Icon={SecurityOutlinedIcon} title="Política de privacidad (otro organizador)">
                        <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
                          Esta política solo se aplica cuando el organizador es «Otro». Si cambias a ACTEDI,
                          la web usa automáticamente la política de ACTEDI.
                        </Typography>
                        <div className="congreso-org-tiles" role="radiogroup" aria-label="Tipo de política de privacidad">
                          <button
                            type="button"
                            role="radio"
                            aria-checked={
                              String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto"
                            }
                            className={
                              "congreso-org-tile" +
                              (String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto"
                                ? " congreso-org-tile--selected"
                                : "")
                            }
                            onClick={() =>
                              setEdicion({ ...edicion, politicaPrivacidadModo: "enlace" })
                            }
                          >
                            <span className="congreso-org-tile__icon-wrap" aria-hidden>
                              <LinkOutlinedIcon className="congreso-org-tile__icon" />
                            </span>
                            <span className="congreso-org-tile__label">Enlace</span>
                            <span className="congreso-org-tile__desc">URL a la política (por idioma).</span>
                          </button>
                          <button
                            type="button"
                            role="radio"
                            aria-checked={
                              String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() === "texto"
                            }
                            className={
                              "congreso-org-tile" +
                              (String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() === "texto"
                                ? " congreso-org-tile--selected"
                                : "")
                            }
                            onClick={() =>
                              setEdicion({ ...edicion, politicaPrivacidadModo: "texto" })
                            }
                          >
                            <span className="congreso-org-tile__icon-wrap" aria-hidden>
                              <EditOutlinedIcon className="congreso-org-tile__icon" />
                            </span>
                            <span className="congreso-org-tile__label">Texto propio</span>
                            <span className="congreso-org-tile__desc">Redacta la política en el editor.</span>
                          </button>
                        </div>

                        {String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto" ? (
                          <MultilanguageTabBlock
                            contentDisabled={delegadoFormLocked}
                            multilanguageTabContent={(lang) => (
                              <TextField
                                label={`URL (${lang})`}
                                value={
                                  (edicion.politicaPrivacidadUrl &&
                                    edicion.politicaPrivacidadUrl[lang]) ||
                                  ""
                                }
                                onChange={(e) =>
                                  setEdicion({
                                    ...edicion,
                                    politicaPrivacidadUrl: {
                                      ...(edicion.politicaPrivacidadUrl || {}),
                                      [lang]: e.target.value,
                                    },
                                  })
                                }
                                InputLabelProps={{ shrink: true }}
                                margin="normal"
                                variant="outlined"
                                fullWidth
                                placeholder="https://…"
                              />
                            )}
                          />
                        ) : (
                          <MultilanguageTabBlock
                            contentDisabled={delegadoFormLocked}
                            multilanguageTabContent={(lang) => (
                              <div className="congreso-edicion-multi-editor-wrap">
                                <Editor
                                  body={
                                    (edicion.politicaPrivacidadHtml &&
                                      edicion.politicaPrivacidadHtml[lang]) ||
                                    ""
                                  }
                                  setBody={(v) =>
                                    handleChangeEditor("politicaPrivacidadHtml", lang, v)
                                  }
                                  className="max-height"
                                  lang={lang}
                                />
                              </div>
                            )}
                          />
                        )}
                      </GeneralApartado>
                    </>
                  ) : null}
                </GeneralApartado>
              </fieldset>
            </div>
          )}

          {mainTab === MAIN_TAB_PRIVACIDAD && (
            <div className="congreso-edicion-tab-panel">
              <fieldset
                disabled={delegadoFormLocked}
                style={{ border: "none", padding: 0, margin: 0, minWidth: 0 }}
              >
                <GeneralApartado Icon={SecurityOutlinedIcon} title="Política de privacidad">
                  <Typography variant="body2" color="textSecondary" component="p" className="mb-2">
                    Cómo se muestra la política de privacidad de esta edición (formularios y pie). Puedes usar
                    un enlace externo o redactar el texto aquí.
                  </Typography>
                  <div
                    className="congreso-org-tiles"
                    role="radiogroup"
                    aria-label="Tipo de política de privacidad"
                  >
                    <button
                      type="button"
                      role="radio"
                      aria-checked={
                        String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto"
                      }
                      className={
                        "congreso-org-tile" +
                        (String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto"
                          ? " congreso-org-tile--selected"
                          : "")
                      }
                      onClick={() =>
                        setEdicion({ ...edicion, politicaPrivacidadModo: "enlace" })
                      }
                    >
                      <span className="congreso-org-tile__icon-wrap" aria-hidden>
                        <LinkOutlinedIcon className="congreso-org-tile__icon" />
                      </span>
                      <span className="congreso-org-tile__label">Enlace</span>
                      <span className="congreso-org-tile__desc">
                        URL a la política (por idioma). Si está vacío, la web usará la de ACTEDI.
                      </span>
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={
                        String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() === "texto"
                      }
                      className={
                        "congreso-org-tile" +
                        (String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() === "texto"
                          ? " congreso-org-tile--selected"
                          : "")
                      }
                      onClick={() =>
                        setEdicion({ ...edicion, politicaPrivacidadModo: "texto" })
                      }
                    >
                      <span className="congreso-org-tile__icon-wrap" aria-hidden>
                        <EditOutlinedIcon className="congreso-org-tile__icon" />
                      </span>
                      <span className="congreso-org-tile__label">Texto propio</span>
                      <span className="congreso-org-tile__desc">
                        Redacta la política con el editor (por idioma).
                      </span>
                    </button>
                  </div>
                </GeneralApartado>

                {String(edicion.politicaPrivacidadModo || "enlace").toLowerCase() !== "texto" ? (
                  <GeneralApartado Icon={LinkOutlinedIcon} title="URL de la política">
                    <MultilanguageTabBlock
                      contentDisabled={delegadoFormLocked}
                      multilanguageTabContent={(lang) => (
                        <TextField
                          label={`URL (${lang})`}
                          value={
                            (edicion.politicaPrivacidadUrl &&
                              edicion.politicaPrivacidadUrl[lang]) ||
                            ""
                          }
                          onChange={(e) =>
                            setEdicion({
                              ...edicion,
                              politicaPrivacidadUrl: {
                                ...(edicion.politicaPrivacidadUrl || {}),
                                [lang]: e.target.value,
                              },
                            })
                          }
                          InputLabelProps={{ shrink: true }}
                          margin="normal"
                          variant="outlined"
                          fullWidth
                          placeholder="https://…"
                          helperText="Enlace que se abrirá desde los formularios y el pie de esta edición."
                        />
                      )}
                    />
                  </GeneralApartado>
                ) : (
                  <GeneralApartado Icon={EditOutlinedIcon} title="Texto de la política">
                    <MultilanguageTabBlock
                      contentDisabled={delegadoFormLocked}
                      multilanguageTabContent={(lang) => (
                        <Editor
                          body={
                            (edicion.politicaPrivacidadHtml &&
                              edicion.politicaPrivacidadHtml[lang]) ||
                            ""
                          }
                          setBody={(v) =>
                            handleChangeEditor("politicaPrivacidadHtml", lang, v)
                          }
                          className="max-height"
                          lang={lang}
                        />
                      )}
                    />
                  </GeneralApartado>
                )}
              </fieldset>
            </div>
          )}

        </CardBody>
      </Card>

      {edicionId && (
        <ConfirmDialog
          title="¿Estás seguro de que quieres eliminar esta edición de congreso?"
          open={openConfirmDialog}
          setOpen={setOpenConfirmDialog}
          onConfirm={() => {
            deleteCongresoEdicion(edicionId)
              .then(() => {
                alertSuccess({ customMessage: "Edición eliminada." });
                history.push("/congresos");
              })
              .catch((error) => {
                alertError({
                  error: error,
                  customMessage: "No se pudo eliminar la edición.",
                });
              });
          }}
        />
      )}

      {edicionId && (
        <CongresoShareDialog
          open={openShareDialog}
          onClose={() => setOpenShareDialog(false)}
          edicionId={edicionId}
        />
      )}
    </>
  );
}
